<template>
    <div id="page-timelog" data-page="timelog">
        <div class="time-management-page">
            <!-- Missing Backlog Source Warning Card -->
            <card-missing-source
            v-if="isMissingBacklogVisible"
            source-type="backlog"
            feature-name="Timelog"
            :project="activeProject"
        />

            <!-- Service Unavailable Placeholder -->
            <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
                <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
                <div class="font-weight-bold mb-1">Service is currently unreachable</div>
                <span class="small">Time logs and calendar will be loaded automatically once the service is restored.</span>
            </div>

            <div v-else>
            <!--Calendar & Time Summary-->
            <b-row class="my-2 my-lg-3 calendar-summary-row" :class="{ 'mobile-insights-collapsed': !showMobileInsights }" align-h="center" align-v="stretch">
                <b-col cols="12" lg="3" class="d-none d-lg-block px-md-4 mb-3 mb-lg-0 calendar-side-col">
                    <b-overlay :show="loadingMonthChart" class="calendar-side-overlay" rounded="lg" opacity="0.6">
                        <template #overlay>
                            <load-spinner text="Loading chart..." size="sm" />
                        </template>
                        <div class="time-progress-card month-worktype-chart">
                            <worktype-doughnut :hours="monthWorkTypeHours" :title="monthWorkTypeChartTitle" :empty-text="emptyMonthChartText"/>
                        </div>
                    </b-overlay>
                </b-col>
                <b-col cols="12" lg="6" class="calendar-center-col d-flex flex-column">
                    <div class="calendar-collapsible-body">
                    <b-overlay :show="loadingCalendarInfo" class="calendar-card-overlay" rounded="lg" opacity="0.6">
                        <template #overlay>
                            <load-spinner text="Loading calendar..." size="sm" />
                        </template>
                        <div class="calendar-month-nav-container position-relative">
                            <div class="calendar-centered-month-header d-flex align-items-center justify-content-center">
                                <b-button variant="outline-secondary"
                                          size="sm"
                                          class="calendar-nav-arrow-btn mr-2"
                                          @click="navigateCalendarMonths(-1)"
                                          :disabled="isCalendarPrevNavDisabled(-1)"
                                          aria-label="Previous month"
                                          title="Previous month"
                                          data-test="btn-calendar-prev-month">
                                    <i class="bi bi-chevron-left"></i>
                                </b-button>
                                <span class="calendar-header-title font-weight-bold mx-2">
                                    {{ calendarCurrentMonthTitle }}
                                </span>
                                <b-button variant="outline-secondary"
                                          size="sm"
                                          class="calendar-nav-arrow-btn ml-2"
                                          @click="navigateCalendarMonths(1)"
                                          aria-label="Next month"
                                          title="Next month"
                                          data-test="btn-calendar-next-month">
                                    <i class="bi bi-chevron-right"></i>
                                </b-button>
                            </div>
                            <b-calendar class="time-manager-calendar" :key="calendarRenderKey" :value="calendarSelectedValue" @input="onCalendarDayPicked" @context="onCalendarContextChange" :block="true" :start-weekday="1"
                                        :date-disabled-fn="calendarDisabledDates" :date-info-fn="calendarDayStatusStyle"
                                        locale="en" :min="calendarMinDate" :initial-date="calendarInitialDate" hide-header>
                            </b-calendar>
                        </div>
                    </b-overlay>
                    <div class="calendar-day-legend">
                        <span class="legend-item"><span class="legend-swatch table-success"></span>Fully logged</span>
                        <span class="legend-item"><span class="legend-swatch table-warning"></span>Partially logged</span>
                        <span class="legend-item"><span class="legend-swatch table-danger"></span>Over capacity</span>
                        <span class="legend-item"><span class="legend-swatch table-info"></span>Absence / Holiday</span>
                        <span class="legend-item"><span class="legend-swatch calendar-legend-weekend"></span>Weekend</span>
                        <span class="legend-item"><span class="legend-swatch legend-swatch-today"></span>Today</span>
                    </div>

                    </div>
                    <!--Selected Date Banner-->
                    <div class="picked-date-banner mt-lg-3 px-2 px-sm-3 py-2 d-flex flex-column">
                        <div class="picked-date-header-row d-flex align-items-center justify-content-between w-100 flex-nowrap">
                            <!-- Selected Date & Status -->
                            <div class="picked-date-main-info d-flex align-items-center flex-nowrap mr-2">
                                <div class="picked-date-calendar-icon mr-2 mr-lg-3 flex-shrink-0">
                                    <i class="bi bi-calendar3"></i>
                                </div>
                                <span class="picked-date-text mr-1 mr-sm-2 text-truncate">
                                    <span class="d-none d-lg-inline">{{ selectedDateFormatted }}</span>
                                    <span class="d-inline d-lg-none">{{ selectedDateFormattedShort }}</span>
                                </span>
                                <b-badge v-if="isPickedDateToday" variant="primary" class="picked-date-pill mr-1">
                                    <i class="bi bi-dot"></i>Today
                                </b-badge>
                                <b-badge v-else-if="isPickedDateWeekend" variant="secondary" class="picked-date-pill mr-1">
                                    Weekend
                                </b-badge>
                                <b-badge v-if="isPickedDateHoliday" variant="danger" class="picked-date-pill mr-1 text-truncate" :title="pickedDateHolidayName || 'Holiday'">
                                    <i class="bi bi-calendar-check mr-1"></i>{{ pickedDateHolidayName || 'Holiday' }}
                                </b-badge>
                                <b-badge v-if="pickedDateAbsenceName" variant="info" class="picked-date-pill text-truncate" :title="pickedDateAbsenceName">
                                    <i class="bi bi-airplane mr-1"></i>{{ pickedDateAbsenceName }}
                                </b-badge>
                            </div>
                            <div class="picked-date-status-wrap ml-auto flex-shrink-0">
                                <div class="picked-date-badge-pill" :class="pickedDateStatus.variantClass">
                                    <i :class="pickedDateStatus.icon" class="status-icon mr-1 mr-sm-2"></i>
                                    <span class="status-label mr-1 d-none d-sm-inline">{{ pickedDateStatus.label }}:</span>
                                    <span class="status-label mr-1 d-inline d-sm-none">{{ pickedDateStatus.shortLabel || pickedDateStatus.label }}:</span>
                                    <strong class="status-value">{{ pickedDateStatus.text }}</strong>
                                </div>
                            </div>
                        </div>
                        <div class="mobile-date-toolbar d-flex d-lg-none flex-wrap align-items-center justify-content-between w-100 mt-1 pt-1">
                            <div class="d-inline-flex align-items-center mobile-day-stepper">
                                <b-button size="sm" variant="outline-secondary" class="mobile-stepper-btn mobile-step-arrow"
                                          :disabled="isPrevDayDisabled"
                                          aria-label="Previous day"
                                          title="Previous day"
                                          data-test="btn-mobile-prev-day"
                                          @click="stepSelectedDay(-1)">
                                    <i class="bi bi-chevron-left"></i>
                                </b-button>
                                <b-button size="sm" variant="outline-primary" class="mobile-stepper-btn mobile-step-today font-weight-bold"
                                          :disabled="isPickedDateToday"
                                          aria-label="Jump to today"
                                          title="Jump to today"
                                          data-test="btn-mobile-today"
                                          @click="pickTodayDay">
                                    Today
                                </b-button>
                                <b-button size="sm" variant="outline-secondary" class="mobile-stepper-btn mobile-step-arrow"
                                          aria-label="Next day"
                                          title="Next day"
                                          data-test="btn-mobile-next-day"
                                          @click="stepSelectedDay(1)">
                                    <i class="bi bi-chevron-right"></i>
                                </b-button>
                            </div>
                            <div class="mobile-mini-metrics d-none d-sm-inline-flex align-items-center small text-muted mx-2">
                                <span>Week: <strong class="text-body">{{ thisWeekLoggedHours }}/{{ thisWeekTotalHours }}h</strong></span>
                                <span class="mx-1">&bull;</span>
                                <span>Month: <strong class="text-body">{{ thisMonthLoggedHours }}/{{ thisMonthTotalHours }}h</strong></span>
                            </div>
                            <b-button size="sm"
                                      :variant="showMobileInsights ? 'primary' : 'outline-secondary'"
                                      class="mobile-insights-toggle-btn font-weight-bold d-inline-flex align-items-center ml-auto"
                                      :aria-expanded="showMobileInsights ? 'true' : 'false'"
                                      aria-label="Toggle calendar"
                                      title="Toggle calendar"
                                      data-test="btn-toggle-mobile-insights"
                                      @click="showMobileInsights = !showMobileInsights">
                                <i class="bi bi-calendar2-range mr-1"></i>
                                <span>{{ showMobileInsights ? 'Hide Calendar' : 'Calendar' }}</span>
                                <i class="bi ml-1" :class="showMobileInsights ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                            </b-button>
                        </div>
                    </div>
                </b-col>
                <b-col cols="12" lg="3" class="d-none d-lg-block px-md-4 mb-3 mb-lg-0 calendar-side-col">
                    <b-overlay :show="loadingTimeLogSummary" class="calendar-side-overlay" rounded="lg" opacity="0.6">
                        <template #overlay>
                            <load-spinner text="Loading summary..." size="sm" />
                        </template>
                        <div class="time-progress-card month-worktype-chart">
                            <div class="time-progress-title">Target progress</div>
                            <time-progress-item label="Today"
                                                :value="todayLoggedHours"
                                                :total="todayTargetCapacity"
                                                :is-weekend-day="isTodayWeekend"
                                                :is-holiday="isTodayHoliday"/>
                            <time-progress-item label="This Week"
                                                period="week"
                                                :period-date="todayDate"
                                                :marker-date="todayDate"
                                                :daily-capacity="dailyCapacity"
                                                :value="thisWeekLoggedHours"
                                                :to-date-value="thisWeekToDateHours"
                                                :future-value="thisWeekFutureHours"
                                                :total="thisWeekTotalHours"/>
                            <time-progress-item label="This Month"
                                                period="month"
                                                :period-date="todayDate"
                                                :marker-date="todayDate"
                                                :period-start-date="startLogDate"
                                                :daily-capacity="dailyCapacity"
                                                :value="thisMonthLoggedHours"
                                                :to-date-value="thisMonthToDateHours"
                                                :future-value="thisMonthFutureHours"
                                                :total="thisMonthTotalHours"/>
                        </div>
                    </b-overlay>
                </b-col>
            </b-row>

            <!--Time Controls-->
            <div v-show="!isHoliday(pickedYMD)">
                <!--Add Time Buttons-->
                <b-row class="my-2 my-lg-3 time-action-row">
                    <b-col cols="12" class="d-flex justify-content-center flex-wrap time-action-col">
                        <b-button variant="outline-info" class="time-action-button"
                                  v-b-popover.hover.top.noninteractive="actionHoverPop('Log work time synced with backlog platform.')"
                                  :disabled="isServiceUnavailable || loadingTimeLogItems || hasModifyingWorkItems() || isSelectedDateInFuture()"
                                  data-test="btn-add-work"
                                  @click="addWorkTimeItem">
                            <i class="bi bi-list-check action-btn-icon"></i><span class="action-btn-label">Work</span><kbd class="ml-1 shortcut-badge">W</kbd>
                        </b-button>
                        <b-button variant="outline-warning" class="time-action-button"
                                  v-b-popover.hover.top.noninteractive="actionHoverPop(hasUnsavedActivityItem() ? 'Save or discard the current unsaved activity before adding another.' : 'Log activity time not accounted in cloud service.')"
                                  :disabled="isServiceUnavailable || loadingTimeLogItems || isSelectedDateInFuture() || hasUnsavedActivityItem()"
                                  data-test="btn-add-activity"
                                  @click="addActivity">
                            <i class="bi bi-journal-text action-btn-icon"></i><span class="action-btn-label">Activity</span><kbd class="ml-1 shortcut-badge">A</kbd>
                        </b-button>
                        <b-button variant="outline-secondary" class="time-action-button"
                                  v-b-popover.hover.top.noninteractive="actionHoverPop('Log time away from work (Leave/PTO).')"
                                  :disabled="isServiceUnavailable || loadingTimeLogItems"
                                  data-test="btn-add-absence"
                                  @click="applyAbsenceAsync">
                            <i class="bi bi-airplane action-btn-icon"></i><span class="action-btn-label">Absence</span><kbd class="ml-1 shortcut-badge">B</kbd>
                        </b-button>
                        <b-button variant="outline-success" class="time-action-button d-none d-lg-inline-flex time-action-export"
                                  v-b-popover.hover.top.noninteractive="actionHoverPop('Export time logs.')"
                                  :disabled="isServiceUnavailable || exportingLogs"
                                  data-test="btn-export-csv"
                                  @click="openExportModal">
                            <b-spinner small v-if="exportingLogs" class="action-btn-icon"></b-spinner>
                            <i v-else class="bi bi-file-earmark-spreadsheet action-btn-icon"></i><span class="action-btn-label">Export</span>
                        </b-button>
                        <b-button variant="outline-warning" class="time-action-button"
                                  v-b-popover.hover.top.noninteractive="actionHoverPop(mismatchCount > 0 ? 'Reconcile hours logged directly in external backlog platform.' : 'Check and reconcile hours logged directly in external backlog platform.')"
                                  :disabled="isServiceUnavailable || loadingTimeLogItems"
                                  data-test="btn-open-reconcile"
                                  @click="openReconcileModal">
                            <div class="action-btn-icon-wrap position-relative d-inline-flex"><i class="bi bi-arrow-repeat action-btn-icon"></i>
                                <b-badge v-if="(!timelogFeatureSettings || (timelogFeatureSettings.enabled !== false && timelogFeatureSettings.showExternalMismatchBadge !== false)) && mismatchCount > 0" variant="warning" class="reconcile-badge-count">{{ mismatchCount }}</b-badge></div><span class="action-btn-label">Reconcile</span>
                        </b-button>
                    </b-col>
                </b-row>

                <!--Time Items-->
                <b-overlay :show="loadingTimeLogItems" class="time-items-overlay" rounded="lg" opacity="0.6">
                    <template #overlay>
                        <load-spinner text="Loading time logs..." />
                    </template>
                    <!--No Items-->
                    <b-row class="my-3" v-if="!loadingTimeLogItems && !timeItemsOfWork.length && !timeItemsOfAbsence.length && !timeItemsOfActivity.length">
                        <b-col cols="12">
                            <grid-empty-state
                                title="No time entries for this date"
                                message="No work items, activities, or absences are logged for the selected date."
                                icon="bi bi-clock-history"
                                :has-active-filters="false"
                                :show-reset="false"
                                :suggestions="timelogEmptySuggestions"
                                :actions="timelogEmptyActions"
                                @action="onTimelogEmptyAction"
                            />
                        </b-col>
                    </b-row>

                    <!--Work Items Grid-->
                    <b-container fluid class="work-items-grid" v-if="!isMissingBacklogVisible && timeItemsOfWork.length" ref="workItemsGrid">
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <h5 class="time-section-title mb-0"><i class="bi bi-list-check mr-2"></i>Work Items</h5>
                            <div v-if="false">
                            <b-button size="sm" variant="outline-secondary"
                                @click="() => openColumnSettings()"
                                title="Customize visible work item columns"
                                data-test="btn-customize-columns">
                                <i class="bi bi-gear"></i>
                            </b-button>
                        </div>
                        </div>
                        <grid-shell ref="workGridShell" :columns="gridColumnsWork" :loading="loadingTimeLogItems" resizable-columns :custom-columns-handler="openColumnSettings" best-fit-test-id="btn-best-fit-time-columns" columns-button-test-id="btn-customize-columns" @best-fit="autoFitTimeGridColumns" @column-resize="onWorkColumnResize" @column-resize-end="onWorkColumnResizeEnd" @column-resize-reset="onWorkColumnResizeReset">
                            <template #view>
                                <span class="small text-muted"><i class="bi bi-list-check mr-1"></i>Work Items</span>
                            </template>
                            <template #view-actions>
                                <b-badge variant="secondary" class="py-1 px-2 mr-1 font-weight-500">
                                    {{ timeItemsOfWork.length }} {{ timeItemsOfWork.length === 1 ? 'item' : 'items' }}
                                </b-badge>
                            </template>
                            <div v-for="group in groupedWorkItems" :key="group.projectName" class="project-work-group w-100">
                                <b-card no-body v-if="!group.isFlat && (!timelogFeatureSettings || (timelogFeatureSettings.enabled !== false && timelogFeatureSettings.showProjectGrouping !== false))" class="my-2 bg-light border">
                                    <b-row class="my-1 grid-row grid-project-row p-2 align-items-center">
                                        <b-col class="px-1 d-flex align-items-center">
                                            <b-button variant="outline-secondary" size="sm" class="py-0 px-2 mr-2" @click="toggleProjectCollapse(group.index)">
                                                <i :class="projectCollapses[group.index] ? 'bi bi-chevron-right' : 'bi bi-chevron-down'"></i>
                                            </b-button>
                                            <i class="bi bi-folder2-open mr-2 text-primary"></i>
                                            <strong>{{ group.projectName }}</strong>
                                            <b-badge variant="secondary" class="ml-2">{{ group.items.length }}</b-badge>
                                        </b-col>
                                    </b-row>
                                </b-card>
                                <b-collapse :visible="!projectCollapses[group.index]">
                                    <ValidationObserver v-slot="{ handleSubmit }">
                                        <b-form v-for="item in group.items" :key="item.vid" @submit.prevent="() => handleSubmit(() => upsertTimeLogAsync(item, TIME_ITEM_TYPE_WORK))">
                                            <div class="time-row-shell" :id="`work-row-${item.vid}`" :class="{ 'time-row-clamped': !expandedWorkTitles[item.vid], 'time-row-shake': shakingWorkItemVid === item.vid }" data-test="grid-row-work" :data-test-id="item.id || item.workItemId || item.vid">
                                <b-row align-h="start" align-v="center" class="grid-row">
                                    <template v-for="col in visibleTimeGridColumns">
                                        <!-- Work Item ID -->
                                        <b-col v-if="col.referenceName === 'System.WorkItem'" :key="col.referenceName" class="grid-col-workitemid d-flex align-items-center">
                                            <button v-if="expandedWorkTitles[item.vid] || isWorkTitleOverflowing(item)"
                                                type="button"
                                                class="row-expand-btn mr-1"
                                                @click.stop="toggleWorkTitle(item.vid)"
                                                :title="expandedWorkTitles[item.vid] ? 'Collapse row' : 'Expand row'">
                                                <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': expandedWorkTitles[item.vid] }"></i>
                                            </button>
                                            <span v-else-if="hasAnyWorkTitleOverflow()" class="row-expand-placeholder mr-1"></span>
                                            <nav-link-button :href="item.url" :id="item.workItemId"
                                                             :type-icon="item.workItemTypeIcon"
                                                             :work-item-type="item.workItemType"
                                                             title="Open work item in Azure DevOps" />
                                        </b-col>

                                        <!-- Title -->
                                        <b-col v-else-if="col.referenceName === 'System.Title'" :key="col.referenceName" class="grid-col-title">
                                            <span class="title-text" :ref="`titleText-${item.vid}`">{{ item.title }}</span>
                                            <span class="title-text title-text-measure" aria-hidden="true">{{ item.title }}</span>
                                        </b-col>

                                        <!-- State -->
                                        <b-col v-else-if="col.referenceName === 'System.State'" :key="col.referenceName" class="grid-col-state">
                                            <status-badge :status="item.state" :custom-color="getStateColor(col, item.state)" />
                                        </b-col>

                                        <!-- Progress -->
                                        <b-col v-else-if="col.referenceName === 'System.Progress'" :key="col.referenceName" class="grid-col-progress">
                                            <time-progress-item compact
                                                                :value="item.getFullCompletedHours()"
                                                                :total="item.getFullCapacityHours()"/>
                                        </b-col>

                                        <!-- Work Type -->
                                        <b-col v-else-if="col.referenceName === 'System.WorkType'" :key="col.referenceName" class="grid-col-worktype">
                                            <label class="mobile-col-caption d-lg-none">{{ col.label || 'Work Type' }}</label>
                                            <ValidationProvider name="Work Type" v-slot="{ errors }"
                                                                :rules="{required: true, distinct_work_type: [timeItemsOfWork, item] }">
                                                <b-form-select size="sm" :id="`wiid-${item.vid}`" name="logTypeName" v-model="item.logTypeName"
                                                               data-test="select-work-type"
                                                               :disabled="hasModifyingOtherWorkItems(item)">
                                                    <b-form-select-option v-for="wt in item.workTypes()" :key="wt" :value="wt">{{ wt }}</b-form-select-option>
                                                </b-form-select>
                                                <b-popover :target="`wiid-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                    {{errors[0]}}
                                                </b-popover>
                                            </ValidationProvider>
                                        </b-col>

                                        <!-- Completed Work -->
                                        <b-col v-else-if="col.referenceName === 'System.Hours'" :key="col.referenceName" class="grid-col-hours">
                                            <label class="mobile-col-caption d-lg-none">{{ col.label || 'Completed Work' }}</label>
                                            <div class="completed-work-row">
                                                <ValidationProvider name="Hours" v-slot="{ errors }"
                                                                    :rules="{required: true, integer: true, min_value: item.getMinHours() }">
                                                    <b-input-group size="sm" class="stepper-input-group">
                                                        <b-input-group-prepend class="d-lg-none">
                                                            <b-button size="sm"
                                                                      variant="outline-secondary"
                                                                      class="stepper-btn stepper-btn-dec"
                                                                      aria-label="Decrease hours"
                                                                      title="Decrease hours"
                                                                      data-test="btn-hours-dec"
                                                                      :disabled="hasModifyingOtherWorkItems(item) || (parseInt(item.hours, 10) || 0) <= (item.getMinHours ? (item.getMinHours() || 0) : 0)"
                                                                      @click="stepHours(item, -1)">
                                                                <i class="bi bi-dash"></i>
                                                            </b-button>
                                                        </b-input-group-prepend>
                                                        <b-form-input size="sm" :id="`wihours-${item.vid}`" name="hours" v-model="item.hours" type="number" :min="0"
                                                                      data-test="input-hours"
                                                                      :disabled="hasModifyingOtherWorkItems(item)">
                                                        </b-form-input>
                                                        <b-input-group-append>
                                                            <b-button size="sm"
                                                                      variant="outline-secondary"
                                                                      class="stepper-btn stepper-btn-inc d-lg-none"
                                                                      aria-label="Increase hours"
                                                                      title="Increase hours"
                                                                      data-test="btn-hours-inc"
                                                                      :disabled="hasModifyingOtherWorkItems(item)"
                                                                      @click="stepHours(item, 1)">
                                                                <i class="bi bi-plus"></i>
                                                            </b-button>
                                                            <b-input-group-text>Hrs</b-input-group-text>
                                                        </b-input-group-append>
                                                    </b-input-group>
                                                    <b-popover :target="`wihours-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                        {{errors[0]}}
                                                    </b-popover>
                                                </ValidationProvider>
                                                <ValidationProvider name="Minutes" v-slot="{ errors }"
                                                                    :rules="{required: true, accepted_quarter_minutes: true }">
                                                    <b-input-group size="sm" class="stepper-input-group">
                                                        <b-input-group-prepend class="d-lg-none">
                                                            <b-button size="sm"
                                                                      variant="outline-secondary"
                                                                      class="stepper-btn stepper-btn-dec"
                                                                      aria-label="Decrease minutes"
                                                                      title="Decrease minutes"
                                                                      data-test="btn-minutes-dec"
                                                                      :disabled="hasModifyingOtherWorkItems(item) || (parseInt(item.minutes, 10) || 0) <= 0"
                                                                      @click="stepMinutes(item, -15)">
                                                                <i class="bi bi-dash"></i>
                                                            </b-button>
                                                        </b-input-group-prepend>
                                                        <b-form-input size="sm" :id="`wiminutes-${item.vid}`" name="minutes" v-model="item.minutes" type="number" step="15" min="0" max="45"
                                                                      data-test="input-minutes"
                                                                      :disabled="hasModifyingOtherWorkItems(item)">
                                                        </b-form-input>
                                                        <b-input-group-append>
                                                            <b-button size="sm"
                                                                      variant="outline-secondary"
                                                                      class="stepper-btn stepper-btn-inc d-lg-none"
                                                                      aria-label="Increase minutes"
                                                                      title="Increase minutes"
                                                                      data-test="btn-minutes-inc"
                                                                      :disabled="hasModifyingOtherWorkItems(item) || (parseInt(item.minutes, 10) || 0) >= 45"
                                                                      @click="stepMinutes(item, 15)">
                                                                <i class="bi bi-plus"></i>
                                                            </b-button>
                                                            <b-input-group-text>Min</b-input-group-text>
                                                        </b-input-group-append>
                                                    </b-input-group>
                                                    <b-popover :target="`wiminutes-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                        {{errors[0]}}
                                                    </b-popover>
                                                </ValidationProvider>
                                            </div>
                                        </b-col>

                                        <!-- Capacity -->
                                        <b-col v-else-if="col.referenceName === 'System.Capacity'" :key="col.referenceName" class="grid-col-changecapacity">
                                            <label class="mobile-col-caption d-lg-none">{{ col.label || 'Capacity' }}</label>
                                            <ValidationProvider name="Capacity" v-slot="{ errors }"
                                                                :rules="{required: true, integer: true, min_value: 0 }">
                                                <b-input-group size="sm" class="stepper-input-group">
                                                    <b-input-group-prepend class="d-lg-none">
                                                        <b-button size="sm"
                                                                  variant="outline-secondary"
                                                                  class="stepper-btn stepper-btn-dec"
                                                                  aria-label="Decrease remaining capacity"
                                                                  title="Decrease remaining capacity"
                                                                  data-test="btn-capacity-dec"
                                                                  :disabled="hasModifyingOtherWorkItems(item) || (parseInt(item.capacityHours, 10) || 0) <= 0"
                                                                  @click="stepCapacity(item, -1)">
                                                            <i class="bi bi-dash"></i>
                                                        </b-button>
                                                    </b-input-group-prepend>
                                                    <b-form-input size="sm" :id="`wicapacity-${item.vid}`" name="capacityHours" v-model="item.capacityHours" type="number" :min="0"
                                                                  data-test="input-capacity"
                                                                  :disabled="hasModifyingOtherWorkItems(item)">
                                                    </b-form-input>
                                                    <b-input-group-append>
                                                        <b-button size="sm"
                                                                  variant="outline-secondary"
                                                                  class="stepper-btn stepper-btn-inc d-lg-none"
                                                                  aria-label="Increase remaining capacity"
                                                                  title="Increase remaining capacity"
                                                                  data-test="btn-capacity-inc"
                                                                  :disabled="hasModifyingOtherWorkItems(item)"
                                                                  @click="stepCapacity(item, 1)">
                                                            <i class="bi bi-plus"></i>
                                                        </b-button>
                                                        <b-input-group-text>Hrs</b-input-group-text>
                                                    </b-input-group-append>
                                                </b-input-group>
                                                <b-popover :target="`wicapacity-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                    {{errors[0]}}
                                                </b-popover>
                                            </ValidationProvider>
                                        </b-col>

                                        <!-- Actions Column (Combined State Action & Log Actions) -->
                                        <b-col v-else-if="col.referenceName === 'System.Action'" :key="col.referenceName" class="grid-col-buttons-actions action-buttons">
                                            <label class="mobile-col-caption d-lg-none">{{ (!col.label || col.label === 'Action') ? 'Actions' : col.label }}</label>
                                            <div class="action-buttons-wrap d-flex align-items-center justify-content-center flex-nowrap">
                                                <!-- State Action Group -->
                                                <b-button-group v-if="!timelogFeatureSettings || (timelogFeatureSettings.enabled !== false && timelogFeatureSettings.showStateQuickActions !== false)" class="mr-1">
                                                    <b-button v-for="act in stateActionsFor(item)"
                                                              :key="act.key || act.targetState"
                                                              size="sm"
                                                              class="md-4"
                                                              :variant="act.variant || 'primary'"
                                                              v-b-popover.hover.top.noninteractive="actionHoverPop(act.label || act.targetState)"
                                                              :aria-label="act.label || act.targetState"
                                                              :title="act.label || act.targetState"
                                                              :data-test="act.dataTest || ('btn-state-' + act.key)"
                                                              :disabled="item.isModifying() || hasModifyingOtherWorkItems(item)"
                                                              @click="setTimeLogStateAsync(item, act.targetState)">
                                                        <i :class="actionIconClass(act.icon)"></i>
                                                    </b-button>
                                                </b-button-group>

                                                <!-- Log Actions Group (Save / Reset / Delete) -->
                                                <b-button-group>
                                                    <b-button type="submit" size="sm" class="md-4" variant="warning" v-b-popover.hover.top.noninteractive="actionHoverPop('Save Work')"
                                                              v-if="item.canSave()"
                                                              aria-label="Save work"
                                                              title="Save Work"
                                                              data-test="btn-save-log"
                                                              :disabled="hasModifyingOtherWorkItems(item)">
                                                        <i class="bi bi-save"></i>
                                                    </b-button>
                                                    <b-button size="sm" class="md-4" variant="primary" v-b-popover.hover.top.noninteractive="actionHoverPop('Reset Changes')" @click="item.resetStash()"
                                                              v-if="item.canReset()"
                                                              aria-label="Reset changes"
                                                              title="Reset Changes"
                                                              data-test="btn-reset-log"
                                                              :disabled="hasModifyingOtherWorkItems(item)">
                                                        <i class="bi bi-arrow-counterclockwise"></i>
                                                    </b-button>
                                                    <b-button size="sm" class="md-4" variant="danger" v-b-popover.hover.top.noninteractive="actionHoverPop(item.isSaved() ? 'Delete Log' : 'Discard Log')" @click="deleteTimeLogAsync(item)"
                                                              v-if="item.canDeleteLog()"
                                                              aria-label="Delete log"
                                                              :title="item.isSaved() ? 'Delete Log' : 'Discard Log'"
                                                              data-test="btn-delete-log"
                                                              :disabled="hasModifyingOtherWorkItems(item)">
                                                        <i class="bi bi-trash"></i>
                                                    </b-button>
                                                </b-button-group>
                                            </div>
                                        </b-col>

                                        <!-- Generic Custom Column -->
                                        <b-col v-else :key="col.referenceName" class="px-1 time-col-custom">
                                            <label class="mobile-col-caption d-lg-none">{{ col.label || col.referenceName }}</label>
                                            <template v-if="isColumnSupportedForWorkItem(col, item)">
                                            <b-form-select v-if="getCustomFieldAllowedValues(col).length > 0"
                                                size="sm"
                                                class="value-color-select"
                                                :style="getValueColorSelectStyle(col, getCustomFieldValue(item, col.referenceName))"
                                                :disabled="hasModifyingOtherWorkItems(item)"
                                                :value="getCustomFieldValue(item, col.referenceName)"
                                                @change="val => setCustomFieldValue(item, col.referenceName, val)">
                                                <b-form-select-option value="">-- None --</b-form-select-option>
                                                <b-form-select-option v-for="av in getCustomFieldAllowedValues(col)" :key="av" :value="av">{{ av }}</b-form-select-option>
                                            </b-form-select>
                                            <b-input-group v-else-if="getColumnMetricSuffix(col) || isNumericColumn(col)" size="sm" class="stepper-input-group">
                                                <b-input-group-prepend class="d-lg-none" v-if="isNumericColumn(col)">
                                                    <b-button size="sm"
                                                              variant="outline-secondary"
                                                              class="stepper-btn stepper-btn-dec"
                                                              :aria-label="`Decrease ${col.label || col.referenceName}`"
                                                              :title="`Decrease ${col.label || col.referenceName}`"
                                                              :disabled="hasModifyingOtherWorkItems(item) || (parseFloat(getCustomFieldValue(item, col.referenceName)) || 0) <= 0"
                                                              @click="stepCustomField(item, col, isIntegerColumn(col) ? -1 : -0.5)">
                                                        <i class="bi bi-dash"></i>
                                                    </b-button>
                                                </b-input-group-prepend>
                                                <b-form-input
                                                    size="sm"
                                                    :type="isNumericColumn(col) ? 'number' : 'text'"
                                                    :min="isNumericColumn(col) ? 0 : null"
                                                    :step="isNumericColumn(col) ? (isIntegerColumn(col) ? '1' : '0.5') : null"
                                                    :disabled="hasModifyingOtherWorkItems(item)"
                                                    :value="getCustomFieldValue(item, col.referenceName)"
                                                    @keydown="onCustomFieldKeydown($event, col)"
                                                    @input="val => setCustomFieldValue(item, col.referenceName, val)">
                                                </b-form-input>
                                                <b-input-group-append>
                                                    <b-button v-if="isNumericColumn(col)"
                                                              size="sm"
                                                              variant="outline-secondary"
                                                              class="stepper-btn stepper-btn-inc d-lg-none"
                                                              :aria-label="`Increase ${col.label || col.referenceName}`"
                                                              :title="`Increase ${col.label || col.referenceName}`"
                                                              :disabled="hasModifyingOtherWorkItems(item)"
                                                              @click="stepCustomField(item, col, isIntegerColumn(col) ? 1 : 0.5)">
                                                        <i class="bi bi-plus"></i>
                                                    </b-button>
                                                    <b-input-group-text v-if="getColumnMetricSuffix(col)">{{ getColumnMetricSuffix(col) }}</b-input-group-text>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <b-form-input v-else
                                                size="sm"
                                                :type="isNumericColumn(col) ? 'number' : 'text'"
                                                :step="isNumericColumn(col) ? '0.5' : null"
                                                :disabled="hasModifyingOtherWorkItems(item)"
                                                :value="getCustomFieldValue(item, col.referenceName)"
                                                @keydown="onCustomFieldKeydown($event, col)"
                                                @input="val => setCustomFieldValue(item, col.referenceName, val)">
                                            </b-form-input>
                                            </template>
                                            <div v-else class="text-center py-1 extra-small" :title="`Field not applicable for ${getWorkItemType(item) || 'this item'}`">
                                                <span class="custom-field-na">&mdash;</span>
                                            </div>
                                        </b-col>
                                    </template>
                                </b-row>
                                </div>
                            </b-form>
                        </ValidationObserver>
                                </b-collapse>
                            </div>
                        </grid-shell>
                    </b-container>

                    <!--Activity Items Grid-->
                    <b-container fluid class="activity-items-grid" v-if="timeItemsOfActivity.length" ref="activityItemsGrid">
                        <h5 class="time-section-title"><i class="bi bi-journal-text mr-2"></i>Activities</h5>
                        <grid-shell :columns="gridColumnsActivity" :loading="loadingTimeLogItems" setup-modal-title="Customize Activity Columns" best-fit-test-id="btn-best-fit-activity-columns" columns-button-test-id="btn-customize-activity-columns">
                            <template #view>
                                <span class="small text-muted"><i class="bi bi-journal-text mr-1"></i>Activities</span>
                            </template>
                            <template #view-actions>
                                <b-badge variant="secondary" class="py-1 px-2 mr-1 font-weight-500">
                                    {{ timeItemsOfActivity.length }} {{ timeItemsOfActivity.length === 1 ? 'activity' : 'activities' }}
                                </b-badge>
                            </template>
                            <ValidationObserver v-slot="{ handleSubmit }">
                                <b-form v-for="(item) in timeItemsOfActivity" :key="item.vid" @submit.prevent="() => handleSubmit(() => upsertTimeLogAsync(item, TIME_ITEM_TYPE_ACTIVITY))">
                                    <div class="time-row-shell" :id="`activity-row-${item.vid}`" :class="{ 'activity-row-shake': shakingActivityVid === item.vid }" data-test="grid-row-activity" :data-test-id="item.id || item.vid">
                                    <b-row align-h="start" align-v="center" class="grid-row">
                                        <b-col class="grid-col-worktype">
                                            <label class="mobile-col-caption d-lg-none">{{ gridColumnsActivity[0].label }}</label>
                                            <b-form-select name="logTypeName" v-model="item.logTypeName" data-test="select-activity-worktype">
                                                <b-form-select-option v-for="wt in item.workTypes()" :key="wt" :value="wt">{{ wt }}</b-form-select-option>
                                            </b-form-select>
                                        </b-col>
                                        <b-col class="grid-col-comment">
                                            <label class="mobile-col-caption d-lg-none">{{ gridColumnsActivity[1].label }}</label>
                                            <b-textarea :id="`actcomment-${item.vid}`"
                                                        :ref="`actcomment-${item.vid}`"
                                                        size="sm"
                                                        name="comments"
                                                        v-model="item.comments"
                                                        rows="1"
                                                        data-test="input-activity-comment"
                                                        @input="resizeActivityComment(item.vid)">
                                            </b-textarea>
                                        </b-col>
                                        <b-col class="grid-col-hours">
                                            <label class="mobile-col-caption d-lg-none">{{ gridColumnsActivity[2].label }}</label>
                                            <div class="completed-work-row">
                                                    <ValidationProvider name="Hours" v-slot="{ errors }"
                                                                        :rules="{required: true, integer: true, min_value: 0 }">
                                                        <b-input-group size="sm" class="stepper-input-group">
                                                            <b-input-group-prepend class="d-lg-none">
                                                                <b-button size="sm"
                                                                          variant="outline-secondary"
                                                                          class="stepper-btn stepper-btn-dec"
                                                                          aria-label="Decrease hours"
                                                                          title="Decrease hours"
                                                                          data-test="btn-act-hours-dec"
                                                                          :disabled="(parseInt(item.hours, 10) || 0) <= 0"
                                                                          @click="stepHours(item, -1)">
                                                                    <i class="bi bi-dash"></i>
                                                                </b-button>
                                                            </b-input-group-prepend>
                                                            <b-form-input size="sm" :id="`acthours-${item.vid}`" name="hours" v-model="item.hours" type="number" :min="0" data-test="input-activity-hours"></b-form-input>
                                                            <b-input-group-append>
                                                                <b-button size="sm"
                                                                          variant="outline-secondary"
                                                                          class="stepper-btn stepper-btn-inc d-lg-none"
                                                                          aria-label="Increase hours"
                                                                          title="Increase hours"
                                                                          data-test="btn-act-hours-inc"
                                                                          @click="stepHours(item, 1)">
                                                                    <i class="bi bi-plus"></i>
                                                                </b-button>
                                                                <b-input-group-text>Hrs</b-input-group-text>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <b-popover :target="`acthours-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                            {{errors[0]}}
                                                        </b-popover>
                                                    </ValidationProvider>
                                                    <ValidationProvider name="Minutes" v-slot="{ errors }"
                                                                        :rules="{required: true, accepted_quarter_minutes: true }">
                                                        <b-input-group size="sm" class="stepper-input-group">
                                                            <b-input-group-prepend class="d-lg-none">
                                                                <b-button size="sm"
                                                                          variant="outline-secondary"
                                                                          class="stepper-btn stepper-btn-dec"
                                                                          aria-label="Decrease minutes"
                                                                          title="Decrease minutes"
                                                                          data-test="btn-act-minutes-dec"
                                                                          :disabled="(parseInt(item.minutes, 10) || 0) <= 0"
                                                                          @click="stepMinutes(item, -15)">
                                                                    <i class="bi bi-dash"></i>
                                                                </b-button>
                                                            </b-input-group-prepend>
                                                            <b-form-input size="sm" :id="`actminutes-${item.vid}`" name="minutes" v-model="item.minutes" type="number" step="15" min="0" max="45" data-test="input-activity-minutes">
                                                            </b-form-input>
                                                            <b-input-group-append>
                                                                <b-button size="sm"
                                                                          variant="outline-secondary"
                                                                          class="stepper-btn stepper-btn-inc d-lg-none"
                                                                          aria-label="Increase minutes"
                                                                          title="Increase minutes"
                                                                          data-test="btn-act-minutes-inc"
                                                                          :disabled="(parseInt(item.minutes, 10) || 0) >= 45"
                                                                          @click="stepMinutes(item, 15)">
                                                                    <i class="bi bi-plus"></i>
                                                                </b-button>
                                                                <b-input-group-text>Min</b-input-group-text>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <b-popover :target="`actminutes-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                            {{errors[0]}}
                                                        </b-popover>
                                                    </ValidationProvider>
                                                </div>
                                            </b-col>
                                            <b-col class="grid-col-buttons action-buttons">
                                                <label class="mobile-col-caption d-lg-none">{{ gridColumnsActivity[3].label }}</label>
                                                <div class="action-buttons-wrap d-flex align-items-center justify-content-center flex-nowrap">
                                                    <b-button-group>
                                                        <b-button type="submit" size="sm" class="md-4" variant="warning" v-b-popover.hover.top.noninteractive="actionHoverPop('Save Activity')"
                                                                  v-if="item.canSave()"
                                                                  aria-label="Save activity"
                                                                  title="Save Activity"
                                                                  data-test="btn-save-activity">
                                                            <i class="bi bi-save"></i>
                                                        </b-button>
                                                        <b-button size="sm" class="md-4" variant="primary" v-b-popover.hover.top.noninteractive="actionHoverPop('Reset Changes')" @click="item.resetStash()"
                                                                  v-if="item.isSaved() && item.canReset()"
                                                                  aria-label="Reset changes"
                                                                  title="Reset Changes"
                                                                  data-test="btn-reset-activity">
                                                            <i class="bi bi-arrow-counterclockwise"></i>
                                                        </b-button>
                                                        <b-button size="sm" class="md-4" variant="danger" v-b-popover.hover.top.noninteractive="actionHoverPop(item.isSaved() ? 'Delete Activity' : 'Discard Activity')" @click="deleteTimeLogAsync(item)"
                                                                  v-if="item.canDeleteLog()"
                                                                  aria-label="Delete activity"
                                                                  :title="item.isSaved() ? 'Delete Activity' : 'Discard Activity'"
                                                                  data-test="btn-delete-activity">
                                                            <i class="bi bi-trash"></i>
                                                        </b-button>
                                                    </b-button-group>
                                                </div>
                                            </b-col>
                                        </b-row>
                                        </div>
                                    </b-form>
                                </ValidationObserver>
                        </grid-shell>
                    </b-container>

                    <!--Absence Items Grid-->
                    <b-container fluid class="absence-items-grid" v-if="timeItemsOfAbsence.length">
                        <h5 class="time-section-title"><i class="bi bi-airplane mr-2"></i>Absences</h5>
                        <grid-shell :columns="gridColumnsAbsence" :loading="loadingTimeLogItems" setup-modal-title="Customize Absence Columns" best-fit-test-id="btn-best-fit-absence-columns" columns-button-test-id="btn-customize-absence-columns">
                            <template #view>
                                <span class="small text-muted"><i class="bi bi-airplane mr-1"></i>Absences</span>
                            </template>
                            <template #view-actions>
                                <b-badge variant="secondary" class="py-1 px-2 mr-1 font-weight-500">
                                    {{ timeItemsOfAbsence.length }} {{ timeItemsOfAbsence.length === 1 ? 'absence' : 'absences' }}
                                </b-badge>
                            </template>
                            <b-form v-for="(item) in timeItemsOfAbsence" :key="item.vid" @submit.prevent="upsertTimeLogAsync(item, TIME_ITEM_TYPE_ABSENCE)">
                                <b-row align-h="start" align-v="center" class="my-1 grid-row" data-test="grid-row-absence" :data-test-id="item.id || item.vid">
                                    <b-col class="grid-col-worktype">
                                        <label class="mobile-col-caption d-lg-none">{{ gridColumnsAbsence[0].label }}</label>
                                        <span class="title-text">{{ item.logTypeName }}</span>
                                    </b-col>
                                    <b-col class="grid-col-hours">
                                        <label class="mobile-col-caption d-lg-none">{{ gridColumnsAbsence[1].label }}</label>
                                        <ValidationProvider name="Hours" v-slot="{ errors }"
                                                            :rules="{required: true, integer: true, min_value: item.getMinHours() }">
                                            <b-input-group size="sm">

                                                <b-form-input :id="`abshours-${item.vid}`" name="hours" v-model="item.hours" type="number" :disabled="true"></b-form-input>
                                                <b-input-group-append>
                                                    <b-input-group-text>Hrs</b-input-group-text>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <b-popover :target="`abshours-${item.vid}`" :show="errors.length > 0 && item.isModifying()" variant="danger" placement="left" triggers="" custom-class="validation-pop">
                                                {{errors[0]}}
                                            </b-popover>
                                        </ValidationProvider>
                                    </b-col>
                                    <b-col class="grid-col-buttons action-buttons">
                                        <label class="mobile-col-caption d-lg-none">{{ gridColumnsAbsence[2].label }}</label>
                                        <div class="action-buttons-wrap d-flex align-items-center justify-content-center flex-nowrap">
                                            <b-button-group>
                                                <b-button size="sm" class="md-4" variant="secondary" v-b-popover.hover.top.noninteractive="actionHoverPop('Change')"
                                                          aria-label="Change absence"
                                                          title="Change"
                                                          data-test="btn-edit-absence"
                                                          @click="applyAbsenceAsync(item)">
                                                        <i class="bi bi-pencil"></i>
                                                    </b-button>
                                            </b-button-group>
                                        </div>
                                    </b-col>
                                </b-row>
                            </b-form>
                        </grid-shell>
                    </b-container>
                </b-overlay>
            </div>


            <public-holiday ref="public-holiday-component" :picked-ymd="pickedYMD" :context-year="contextYear"></public-holiday>
        </div>
        </div>

        <!--Modals-->
        <pick-work-item-modal ref="pick-wi-modal"></pick-work-item-modal>
        <apply-absence-modal ref="apply-abs-modal"></apply-absence-modal>

        <!-- Export Time Logs Modal -->
        <b-modal v-model="showExportModal" title="Export Time Logs" centered size="md" data-test="modal-export-timelog">
            <div>
                <div v-if="isViewOnlyMode || hasFreeDailyQuota" class="alert alert-warning py-2 px-3 small d-flex align-items-center mb-3" data-test="alert-export-90day-limit">
                    <i class="bi bi-info-circle-fill text-warning mr-2" style="font-size: 1.1rem;"></i>
                    <div>
                        <strong>Free Tier Notice:</strong> History and exports are limited to the past <strong>90 days</strong>. Upgrading to the <strong>Pro Plan</strong> unlocks unlimited historical exports.
                    </div>
                </div>

                <b-form-group label="Date Range" label-class="font-weight-bold">
                    <b-form-radio-group v-model="exportForm.scope" stacked>
                        <b-form-radio value="month">Current Month ({{ formatCurrentMonthDisplay }})</b-form-radio>
                        <b-form-radio value="picked" :disabled="!pickedYMD">Selected Day ({{ pickedYMD || 'None' }})</b-form-radio>
                        <b-form-radio value="all">All Time (Whole History)</b-form-radio>
                        <b-form-radio value="custom">Custom Range</b-form-radio>
                    </b-form-radio-group>
                </b-form-group>

                <div v-if="exportForm.scope === 'custom'" class="border rounded p-3 mb-3 bg-light">
                    <b-row>
                        <b-col cols="6">
                            <b-form-group label="From Date" label-size="sm" class="mb-0">
                                <b-form-datepicker v-model="exportForm.fromDate" size="sm" reset-button close-button locale="en"></b-form-datepicker>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6">
                            <b-form-group label="To Date" label-size="sm" class="mb-0">
                                <b-form-datepicker v-model="exportForm.toDate" size="sm" reset-button close-button locale="en"></b-form-datepicker>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <b-form-group label="Project Filter" label-class="font-weight-bold">
                    <b-form-select v-model="exportForm.projectId" :options="exportProjectOptions">
                        <template #first>
                            <b-form-select-option :value="null">All Projects</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>

                <div class="small text-muted">
                    <i class="bi bi-info-circle mr-1"></i>Exports CSV with UTF-8 BOM encoding for seamless Microsoft Excel and spreadsheet compatibility.
                </div>
            </div>

            <template #modal-footer="{ cancel }">
                <b-button variant="secondary" @click="cancel" :disabled="exportingLogs">
                    Cancel
                </b-button>
                <b-button variant="success" @click="executeExport" :disabled="exportingLogs" data-test="btn-submit-export" :data-state="exportingLogs ? 'loading' : 'idle'">
                    <b-spinner small v-if="exportingLogs" class="mr-1"></b-spinner>
                    <i v-else class="bi bi-download mr-1"></i>Download CSV
                </b-button>
            </template>
        </b-modal>

        <modal-task-columns
            ref="modalTimeColumns"
            modal-id="modal-time-columns"
            modal-title="Customize Work Item Columns & Grid Setup"
            subtitle="Drag and drop column badges below to customize their display sequence in the Timelog Work Items grid. Core system columns cannot be removed, and the Action column always remains fixed at the end."
            save-endpoint="/TimeManagement/SaveTimeGridColumns"
            :default-columns="DEFAULT_TIME_GRID_COLUMNS"
            :current-columns="activeTimeGridColumns"
            :feature-config="timelogFeatureConfig"
            @columns-saved="onTimeColumnsSaved"
            @feature-settings-saved="onTimelogFeatureSettingsSaved">
        </modal-task-columns>

        <modal-reconcile-logs
            ref="reconcile-modal"
            :start-log-date="startLogDate"
            :work-types="loadedWorkTypes"
            :daily-capacity="dailyCapacity"
            :calendar-day-statuses="calendarDayStatuses"
            :public-holidays="isHoliday"
            @mismatches-loaded="mismatchesList = $event"
            @reconciled="onLogsReconciled">
        </modal-reconcile-logs>
    </div>
</template>

<script>
    import _ from "lodash";
    import { v4 as uuidv4 } from "uuid";
    import { format } from "date-fns";
    import { extend } from 'vee-validate';
    import accountMixin from "../mixinAccount";
    import toastMixin from "../mixinToast";
    import eventBus from "../event-bus";
    import commonMixin from "../mixinCommon";
    import workTypeChartMixin from "../mixinWorkTypeChart";
    import workItemStateMixin from "../mixinWorkItemState";
    import PickWorkItemModal from "./modal-pick-work-item.vue";
    import ApplyAbsenceModal from "./modal-apply-absence.vue";
    import PublicHoliday from './public-holiday.vue';
    import WorktypeDoughnut from "./chart-worktype-doughnut.vue";
    import TimeProgressItem from "./time-progress-item.vue";
    import GridShell from "./grid-shell.vue";
    import NavLinkButton from "./nav-link-button.vue";
    import StatusBadge from "./status-badge.vue";
    import CardMissingSource from "./card-missing-source.vue";
    import ModalTaskColumns, { DEFAULT_TIME_GRID_COLUMNS, DEFAULT_STATE_COLORS, DEFAULT_STATE_VALUES, getStoredStateColors, setStoredStateColors } from "./modal-task-columns.vue";
    import ModalReconcileLogs from "./modal-reconcile-logs.vue";
    import GridEmptyState from "./grid-empty-state.vue";
        
    extend("distinct_work_type", {
        validate(value, args) {
            let allWorkItems = args["allWorkItems"];
            let thisWorkItem = args["thisWorkItem"];
            let val = value.toLowerCase();
            const count = _.filter(allWorkItems, i => i && i.workItemId == thisWorkItem.workItemId && i.logTypeName && i.logTypeName.toLowerCase() === val).length;
            return count <= 1;
        },
        params: ['allWorkItems', 'thisWorkItem'],
        message: "Cannot have multiple items with same {_field_} value!"
    });
    extend("accepted_quarter_minutes", {
        validate(value) {
            const val = parseInt(value);
            return val === 0 || val === 15 || val === 30 || val === 45;
        },
        message: "Can only have quarter of an hour value!"
    });

    export default {
        name: 'page-timelog',
        mixins: [accountMixin, toastMixin, commonMixin, workTypeChartMixin, workItemStateMixin],
        components: {
            "pick-work-item-modal": PickWorkItemModal,
            "apply-absence-modal": ApplyAbsenceModal,
            "public-holiday": PublicHoliday,
            "worktype-doughnut": WorktypeDoughnut,
            "time-progress-item": TimeProgressItem,
            "grid-shell": GridShell,
            "nav-link-button": NavLinkButton,
            "status-badge": StatusBadge,
            "card-missing-source": CardMissingSource,
            "modal-task-columns": ModalTaskColumns,
            "modal-reconcile-logs": ModalReconcileLogs,
            "grid-empty-state": GridEmptyState
        },
        data() {
            return {
                showMobileInsights: false,
                DEFAULT_TIME_GRID_COLUMNS,
                activeTimeGridColumns: DEFAULT_TIME_GRID_COLUMNS.map(c => ({ ...c, width: c.width || '' })),
                discoveredCustomFields: [],
                loadingTimeGridColumns: false,
                modalInitialSearchLoaded: false,
                selectedDate: null,
                pickedYMD: null,
                contextYear: null,
                lastPickedYMD: null,
                lastActiveYMD: null,
                timeItems: [],
                timeItemsOfWork: [],
                timeItemsOfAbsence: [],
                timeItemsOfActivity: [],
                shakingActivityVid: null,
                shakingWorkItemVid: null,
                searchDropdownOpen: false,
                gridColumnsAbsence: [
                    { key: "worktype", label: "Absence Type", width: "180px" },
                    { key: "hours", label: "Duration", width: "160px" },
                    { key: "buttons", label: "Action", width: "48px" }
                ],
                gridColumnsActivity: [
                    { key: "worktype", label: "Activity Type", width: "260px" },
                    { key: "comment", label: "Comment", width: "minmax(4rem, 1fr)" },
                    { key: "hours", label: "Duration", width: "220px" },
                    { key: "buttons", label: "Action", width: "48px" }
                ],
                item: { commits: [] },
                loadedWorkTypes: [],
                loadedAbsenceTypes: [],
                boolOptions: ["Yes", "No"],
                startLogDate: null,
                dailyCapacity: 8,
                loadingTimeLogItems: false,
                loadingTimeLogSummary: false,
                loadingCalendarInfo: false,
                totalSavedHours: 0,
                todayLoggedHours: 0,
                thisWeekLoggedHours: 0,
                thisWeekToDateHours: 0,
                thisWeekFutureHours: 0,
                thisWeekTotalHours: 0,
                thisMonthLoggedHours: 0,
                thisMonthToDateHours: 0,
                thisMonthFutureHours: 0,
                thisMonthTotalHours: 0,
                monthWorkTypeHours: [],
                loadingMonthChart: false,
                monthWorkTypeRequestId: 0,
                currentDay: null,
                calendarMinDate: null,
                calendarViewDate: null,
                calendarDayStatuses: [],
                calendarHideHeader: true,
                pickWorkItemModal: {},
                absenceModal: {},
                publicHolidayComponent: {},
                lastLoadedDayProgressParams: {},
                expandedWorkTitles: {},
                workTitleOverflowMap: {},
                isHydratingTimeItems: false,
                isRevertingCalendarDate: false,
                calendarRenderKey: 0,
                projectCollapses: {},
                showExportModal: false,
                exportingLogs: false,
                exportProjectsLoaded: false,
                exportProjectOptions: [],
                exportForm: {
                    scope: 'month',
                    projectId: null,
                    fromDate: null,
                    toDate: null
                },
                mismatchesList: [],
                loadingMismatches: false,
                timelogFeatureSettings: this.loadStoredTimelogFeatureSettings()
            };
        },
        watch: {
            timeItems: {
                handler(logItems) {
                    const numericValues = logItems
                        .map(item => parseFloat(item.getStashedHoursWithMinutes()))
                        .filter(value => !isNaN(value));
                    this.totalSavedHours = _.sum(numericValues);
                    this.timeItemsOfWork = _.orderBy(_.filter(logItems, (i) => i.isWorkItem()), ['clientAddedAt'], ['desc']);
                    this.timeItemsOfAbsence = _.filter(logItems, (i) => i.isAbsenceItem());
                    this.timeItemsOfActivity = _.orderBy(_.filter(logItems, (i) => i.isActivityItem()), ['clientAddedAt'], ['desc']);
                    if (!this.isHydratingTimeItems)
                        this.persistPendingTimeItems(this.pickedYMD);
                    this.resizeAllActivityComments();
                },
                deep: true
            },
            globalProjectId: {
                async handler() {
                    if (!this.hasBacklogSource) return;
                    try {
                        await Promise.all([
                            this.loadConfiguredColumns(),
                            this.detectWorkItemMismatchesAsync()
                        ]);
                    } catch (e) {
                        // Service unavailable or fetch failure handled by banner/mixins
                    }
                }
            },
            '$route.query.date'(newDate) {
                if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate) && newDate !== this.pickedYMD) {
                    this.onCalendarDayPicked(newDate);
                }
            },
            calendarViewDate: {
                handler(newVal, oldVal) {
                    if (!newVal || !oldVal) return;
                    const newY = newVal.getFullYear();
                    const newM = newVal.getMonth();
                    const oldY = oldVal.getFullYear();
                    const oldM = oldVal.getMonth();
                    if (newY !== oldY || newM !== oldM) {
                        this.loadMonthWorkTypeHoursAsync(newVal);
                    }
                }
            }
        },
        mounted() {
            this.pickWorkItemModal = this.$refs["pick-wi-modal"];
            this.absenceModal = this.$refs["apply-abs-modal"];
            this.publicHolidayComponent = this.$refs["public-holiday-component"];
            window.addEventListener("resize", this.onTimeManagerWindowResize);
            this.resizeAllActivityComments();

            this.handleTimeManagerKeydown = (e) => {
                const tag = e.target?.tagName;
                const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable;
                if (isInput) return;

                if (this.isHoliday && this.isHoliday(this.pickedYMD)) return;

                const key = (e.key || '').toLowerCase();
                const code = e.code || '';

                // Add Work: 'w' or 'W' or Alt+W
                if ((key === 'w' || code === 'KeyW') && !e.ctrlKey && !e.metaKey) {
                    if (!this.loadingTimeLogItems && !this.hasModifyingWorkItems() && !this.isSelectedDateInFuture()) {
                        e.preventDefault();
                        this.addWorkTimeItem();
                    }
                    return;
                }

                // Add Activity: 'a' or 'A' or Alt+A
                if ((key === 'a' || code === 'KeyA') && !e.ctrlKey && !e.metaKey) {
                    if (!this.loadingTimeLogItems && !this.isSelectedDateInFuture() && !this.hasUnsavedActivityItem()) {
                        e.preventDefault();
                        this.addActivity();
                    }
                    return;
                }

                // Add Absence: 'b' or 'B' or Alt+B
                if ((key === 'b' || code === 'KeyB') && !e.ctrlKey && !e.metaKey) {
                    if (!this.loadingTimeLogItems) {
                        e.preventDefault();
                        this.applyAbsenceAsync();
                    }
                    return;
                }
            };
            window.addEventListener("keydown", this.handleTimeManagerKeydown);
        },
        beforeUnmount() {
            window.removeEventListener("resize", this.onTimeManagerWindowResize);
            if (this.handleTimeManagerKeydown) {
                window.removeEventListener("keydown", this.handleTimeManagerKeydown);
            }
            if (this.onServiceReconnected) {
                eventBus.$off('service-reconnected', this.onServiceReconnected);
            }
            if (this.onWindowFocusMismatches) {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('focus', this.onWindowFocusMismatches);
                }
                if (typeof document !== 'undefined') {
                    document.removeEventListener('visibilitychange', this.onWindowFocusMismatches);
                }
            }
        },
        computed: {
            timelogEmptySuggestions() {
                return [
                    "Select a different day from the calendar or use the date navigator.",
                    "Log work items directly from your sprint backlog using the 'Work' button.",
                    "Record administrative, review, or ad-hoc time using the 'Activity' button.",
                    "Record leave or holidays using the 'Absence' button."
                ];
            },
            timelogEmptyActions() {
                const actions = [];
                if (!this.isServiceUnavailable && !this.loadingTimeLogItems && !this.hasModifyingWorkItems() && !this.isSelectedDateInFuture()) {
                    actions.push({ text: 'Add Work', icon: 'bi bi-list-check', action: 'add-work', variant: 'outline-info' });
                }
                if (!this.isServiceUnavailable && !this.loadingTimeLogItems && !this.isSelectedDateInFuture() && !this.hasUnsavedActivityItem()) {
                    actions.push({ text: 'Add Activity', icon: 'bi bi-journal-text', action: 'add-activity', variant: 'outline-warning' });
                }
                if (!this.isServiceUnavailable && !this.loadingTimeLogItems) {
                    actions.push({ text: 'Add Absence', icon: 'bi bi-airplane', action: 'add-absence', variant: 'outline-secondary' });
                }
                return actions;
            },
            activeProject() {
                return this.globalProject;
            },
            hasBacklogSetup() {
                return !!this.$store.state.hasBacklogSetup;
            },
            isMissingBacklogVisible() {
                if (!this.isAuthenticated) return false;
                if (!this.activeProject) return true;
                return !this.activeProject.backlogLinkId || !this.activeProject.backlogProjectId;
            },
            hasBacklogSource() {
                return !this.isMissingBacklogVisible;
            },
            isServiceUnavailable() {
                return !!this.$store.state.isServiceUnavailable;
            },
            calendarInitialDate() {
                return this.calendarViewDate || this.newDate(this.pickedYMD);
            },
            calendarSelectedValue() {
                if (!this.pickedYMD || !this.calendarViewDate)
                    return this.pickedYMD;
                const picked = this.newDate(this.pickedYMD);
                const view = this.calendarViewDate;
                if (picked.getFullYear() === view.getFullYear() && picked.getMonth() === view.getMonth())
                    return this.pickedYMD;
                return "";
            },
            isCalendarCurrentMonthDisplayed() {
                const view = this.calendarViewDate || this.newDate();
                const now = this.newDate();
                return view.getFullYear() === now.getFullYear() && view.getMonth() === now.getMonth();
            },
            monthWorkTypeChartTitle() {
                if (this.isCalendarCurrentMonthDisplayed) {
                    return `Breakdown hours · This month`;
                }
                const view = this.calendarViewDate || this.newDate();
                return `Breakdown hours · ${format(view, "MMMM yyyy")}`;
            },
            emptyMonthChartText() {
                if (this.isCalendarCurrentMonthDisplayed) {
                    return "No logged hours this month";
                }
                const view = this.calendarViewDate || this.newDate();
                return `No logged hours in ${format(view, "MMMM yyyy")}`;
            },
            formatCurrentMonthDisplay() {
                const view = this.calendarViewDate || this.newDate();
                return format(view, "MMMM yyyy");
            },
            todayDate() {
                return this.newDate();
            },
            todayYMD() {
                return format(this.todayDate, "yyyy-MM-dd");
            },
            currentWeekRange() {
                const today = this.todayDate;
                if (!today) return { startYMD: '', endYMD: '' };
                const day = today.getDay();
                const diffToMonday = day === 0 ? -6 : 1 - day;
                const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday);
                const sunday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday + 6);
                return {
                    startYMD: format(monday, "yyyy-MM-dd"),
                    endYMD: format(sunday, "yyyy-MM-dd")
                };
            },
            groupedWorkItems() {
                if (!this.timeItemsOfWork || !this.timeItemsOfWork.length) return [];
                const groups = _.groupBy(this.timeItemsOfWork, item => item.projectName || item.projectId || 'Default Project');
                const groupKeys = Object.keys(groups);
                const isFlat = groupKeys.length <= 1;
                return groupKeys.map((name, index) => ({
                    index,
                    projectName: name,
                    isFlat,
                    items: groups[name]
                }));
            },
            visibleTimeGridColumns() {
                return (this.activeTimeGridColumns || []).filter(c => !c.hidden && c.isVisible !== false);
            },
            gridColumnsWork() {
                return (this.visibleTimeGridColumns || [])
                    .filter(col => col.referenceName !== 'System.StateAction')
                    .map(col => {
                    const ref = col.referenceName;
                    const customPx = col && col.width && /^\d+(\.\d+)?px$/i.test(String(col.width).trim()) ? String(col.width).trim() : '';
                    if (ref === 'System.WorkItem') {
                        const label = (!col.label || col.label === 'Work Item') ? 'Item' : col.label;
                        return { key: "workitemid", label, width: customPx || "5.5rem", referenceName: ref };
                    }
                    if (ref === 'System.Title') {
                        return { key: "title", label: col.label || "Title", width: customPx || "minmax(18rem, 1fr)", headerClass: "text-left", referenceName: ref };
                    }
                    if (ref === 'System.State') {
                        return { key: "state", label: col.label || "State", width: customPx || "7.25rem", referenceName: ref };
                    }
                    if (ref === 'System.Progress') {
                        const label = (!col.label || col.label === 'Overall Progress') ? 'Progress' : col.label;
                        return { key: "progress", label, width: customPx || "9rem", referenceName: ref };
                    }
                    if (ref === 'System.WorkType') {
                        return { key: "worktype", label: col.label || "Work Type", width: customPx || "7.5rem", referenceName: ref };
                    }
                    if (ref === 'System.Hours') {
                        return { key: "hours", label: col.label || "Completed Work", width: customPx || "12.25rem", referenceName: ref };
                    }
                    if (ref === 'System.Capacity') {
                        return { key: "changecapacity", label: col.label || "Capacity", width: customPx || "6.5rem", referenceName: ref };
                    }
                    if (ref === 'System.Action') {
                        return { key: "buttons-actions", label: (!col.label || col.label === 'Action') ? 'Actions' : col.label, width: customPx || "8.5rem", referenceName: ref };
                    }
                    const sanitizedKey = 'custom-' + ref.toLowerCase().replace(/[^a-z0-9]/g, '');
                    return {
                        key: sanitizedKey,
                        label: col.label || ref,
                        width: customPx || ((this.isNumericColumn(col) || this.getColumnMetricSuffix(col) || this.columnFieldType(col) === 'boolean') ? "7.5rem" : "9.5rem"),
                        referenceName: ref
                    };
                });
            },
            effectiveSelectedDate() {
                if (this.pickedYMD) return this.newDate(this.pickedYMD);
                return this.selectedDate || this.newDate();
            },
            selectedDateFormatted() {
                const d = this.effectiveSelectedDate;
                if (!d) return "";
                return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
            },
            calendarCurrentMonthTitle() {
                const view = this.calendarViewDate || this.effectiveSelectedDate || this.newDate();
                return format(view, "MMMM yyyy");
            },
            selectedDateFormattedShort() {
                if (!this.pickedYMD) return '';
                try {
                    const date = this.newDate(this.pickedYMD);
                    return format(date, 'EEE, MMM d');
                } catch (e) {
                    return this.pickedYMD;
                }
            },
            isPrevDayDisabled() {
                if (!this.startLogDate || !this.pickedYMD) return false;
                try {
                    const current = this.newDate(this.pickedYMD);
                    current.setDate(current.getDate() - 1);
                    const prevYMD = format(current, "yyyy-MM-dd");
                    return prevYMD < this.startLogDate;
                } catch (e) {
                    return false;
                }
            },
            isPickedDateToday() {
                return this.pickedYMD === this.todayYMD;
            },
            isPickedDateFuture() {
                if (this.pickedYMD && this.todayYMD) {
                    return this.pickedYMD > this.todayYMD;
                }
                const d = this.effectiveSelectedDate;
                if (!d || !this.todayDate) return false;
                return d > this.todayDate;
            },
            isTodayWeekend() {
                const d = this.todayDate;
                if (!d) return false;
                const day = d.getDay();
                return day === 0 || day === 6;
            },
            isTodayHoliday() {
                return this.isHoliday(this.todayYMD);
            },
            todayTargetCapacity() {
                if (this.isTodayWeekend || this.isTodayHoliday) {
                    return 0;
                }
                return this.dailyCapacity;
            },
            isPickedDateWeekend() {
                const d = this.effectiveSelectedDate;
                if (!d) return false;
                const day = d.getDay();
                return day === 0 || day === 6;
            },
            isPickedDateHoliday() {
                return this.isHoliday(this.pickedYMD);
            },
            pickedDateHolidayName() {
                const comp = this.publicHolidayComponent || this.$refs?.["public-holiday-component"];
                if (!comp || typeof comp.getPublicHoliday !== "function") return null;
                const h = comp.getPublicHoliday(this.pickedYMD);
                return h ? (h.localName || h.name || "Public Holiday") : null;
            },
            pickedDateAbsenceName() {
                if (this.timeItemsOfAbsence && this.timeItemsOfAbsence.length > 0) {
                    const item = this.timeItemsOfAbsence[0];
                    const name = typeof item?.getWorkTypeName === 'function'
                        ? item.getWorkTypeName()
                        : item?.logTypeName;
                    return name || 'Absence';
                }
                const dayStat = _.find(this.calendarDayStatuses, p => p.date === this.pickedYMD);
                if (dayStat && (dayStat.style === 'absence' || dayStat.isAbsence)) {
                    return 'Absence';
                }
                return null;
            },
            pickedDateLoggedHours() {
                return Math.round((Number(this.totalSavedHours) || 0) * 100) / 100;
            },
            pickedDateVariance() {
                const logged = this.pickedDateLoggedHours;
                const target = this.dailyCapacity;
                if (this.isPickedDateHoliday) {
                    return {
                        diff: logged,
                        status: logged > 0 ? 'holiday-logged' : 'holiday-off',
                        hours: logged
                    };
                }
                if (this.isPickedDateWeekend) {
                    return {
                        diff: logged,
                        status: logged > 0 ? 'weekend-logged' : 'weekend-off',
                        hours: logged
                    };
                }
                if (this.isPickedDateFuture) {
                    return {
                        diff: 0,
                        status: 'future',
                        hours: target
                    };
                }
                const diff = logged - target;
                if (diff > 0) {
                    return { diff, status: 'overspend', hours: diff };
                } else if (diff === 0 && logged > 0) {
                    return { diff: 0, status: 'met', hours: 0 };
                } else {
                    return { diff, status: 'behind', hours: Math.abs(diff) };
                }
            },
            pickedDateStatus() {
                const v = this.pickedDateVariance;
                if (v.status === 'future') {
                    return {
                        variantClass: 'status-capacity',
                        icon: 'bi bi-clock',
                        label: 'Capacity',
                        shortLabel: 'Cap',
                        text: `${this.dailyCapacity || 8}h`
                    };
                }
                if (v.status === 'holiday-logged') {
                    return {
                        variantClass: 'status-info',
                        icon: 'bi bi-plus-circle-fill',
                        label: 'Holiday Logged',
                        shortLabel: 'Logged',
                        text: `+${v.hours.toFixed(1)}h`
                    };
                }
                if (v.status === 'holiday-off') {
                    return {
                        variantClass: 'status-weekend',
                        icon: 'bi bi-calendar2-minus',
                        label: 'Holiday',
                        shortLabel: 'Holiday',
                        text: 'Off'
                    };
                }
                if (v.status === 'overspend') {
                    return {
                        variantClass: 'status-overspend',
                        icon: 'bi bi-arrow-up-circle-fill',
                        label: 'Over Capacity',
                        shortLabel: 'Over',
                        text: `+${v.hours.toFixed(1)}h`
                    };
                }
                if (v.status === 'met') {
                    return {
                        variantClass: 'status-met',
                        icon: 'bi bi-check-circle-fill',
                        label: 'Capacity Met',
                        shortLabel: 'Met',
                        text: '100%'
                    };
                }
                if (v.status === 'behind') {
                    return {
                        variantClass: 'status-behind',
                        icon: 'bi bi-arrow-down-circle-fill',
                        label: 'Hours Behind',
                        shortLabel: 'Behind',
                        text: `${v.hours.toFixed(1)}h`
                    };
                }
                if (v.status === 'weekend-logged') {
                    return {
                        variantClass: 'status-info',
                        icon: 'bi bi-plus-circle-fill',
                        label: 'Weekend Logged',
                        shortLabel: 'Logged',
                        text: `+${v.hours.toFixed(1)}h`
                    };
                }
                return {
                    variantClass: 'status-weekend',
                    icon: 'bi bi-calendar2-minus',
                    label: 'Weekend',
                    shortLabel: 'Weekend',
                    text: 'Off'
                };
            },
            mismatchCount() {
                return (this.mismatchesList || []).length;
            },
            timelogFeatureConfig() {
                const s = this.timelogFeatureSettings || {};
                return {
                    storageKey: 'vt_timelog_feature_config',
                    title: 'Timelog Work Item Signals & Controls',
                    subtitle: 'Control inline workflow transition buttons, external reconciliation mismatch badges, and multi-project grouping headers.',
                    icon: 'bi bi-clock-history',
                    enabled: s.enabled !== false,
                    masterTestId: 'toggle-timelog-features-master',
                    items: [
                        {
                            key: 'showStateQuickActions',
                            label: 'Inline Workflow State Quick-Actions',
                            description: 'Display one-click workflow state transition buttons directly in the Work Items Action column.',
                            icon: 'bi bi-lightning-charge-fill',
                            previewBadge: 'Move State',
                            previewVariant: 'emerald',
                            enabled: s.showStateQuickActions !== false,
                            testId: 'toggle-timelog-state-actions'
                        },
                        {
                            key: 'showExternalMismatchBadge',
                            label: 'External Hours Mismatch Counter Badge',
                            description: 'Show a live warning counter badge on the Reconcile button when external backlog hours diverge from local time logs.',
                            icon: 'bi bi-arrow-repeat',
                            previewBadge: 'Reconcile 2',
                            previewVariant: 'amber',
                            enabled: s.showExternalMismatchBadge !== false,
                            testId: 'toggle-timelog-mismatch-badge'
                        },
                        {
                            key: 'showProjectGrouping',
                            label: 'Multi-Project Collapsible Group Headers',
                            description: 'Group logged work items under collapsible project headers when entries span multiple projects.',
                            icon: 'bi bi-folder2-open',
                            previewBadge: 'Project Group',
                            previewVariant: 'sky',
                            enabled: s.showProjectGrouping !== false,
                            testId: 'toggle-timelog-project-grouping'
                        }
                    ]
                };
            },
            totalMismatchHours() {
                const sum = (this.mismatchesList || []).reduce((acc, curr) => acc + (Number(curr.deltaHours) || 0), 0);
                return Math.round(sum * 100) / 100;
            }
        },
        created() {
            this.onServiceReconnected = async () => {
                if (this.hasBacklogSource) {
                    try {
                        await Promise.all([
                            this.loadTimeLogSummaryAsync(),
                            this.loadMonthWorkTypeHoursAsync(),
                            this.loadTimeLogItemsByDateAsync(),
                            this.loadCalendarDayProgressAsync(),
                            this.detectWorkItemMismatchesAsync()
                        ]);
                    } catch (e) {
                        // ignore
                    }
                }
            };
            eventBus.$on('service-reconnected', this.onServiceReconnected);
            this.onWindowFocusMismatches = () => {
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
                if (this.hasBacklogSource && this.activeProject && !this.loadingMismatches) {
                    this.detectWorkItemMismatchesAsync();
                }
            };
            if (typeof window !== 'undefined') {
                window.addEventListener('focus', this.onWindowFocusMismatches);
            }
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', this.onWindowFocusMismatches);
            }
        },
        async beforeMount() {
            if (!this.isAuthenticated)
                return;

            let targetDate;
            const queryDate = this.$route && this.$route.query && this.$route.query.date;
            if (queryDate && /^\d{4}-\d{2}-\d{2}$/.test(queryDate)) {
                targetDate = this.newDate(queryDate);
                this.$store.commit('setLastPickedCalendarDate', queryDate);
            } else {
                targetDate = this.newDate(this.$store.state.lastPickedCalendarDate);
            }
            this.contextYear = targetDate.getFullYear();
            this.pickedYMD = format(targetDate, "yyyy-MM-dd");
            this.currentDay = targetDate;
            this.calendarViewDate = targetDate;
            this.calendarMinDate = null;
            setInterval(() => {
                this.checkDayChange();
            }, 60 * 1000); // every minute

            this.loadingMonthChart = true;
            this.loadingTimeLogSummary = true;
            this.loadingCalendarInfo = true;
            this.loadingTimeLogItems = true;

            try {
                await this.$http.get("/Common/ListMetadata")
                    .then(resp => {
                        this.loadedWorkTypes = resp.data.workTypes;
                        this.loadedAbsenceTypes = resp.data.absenceTypes;
                        this.loadedActivityTypes = resp.data.activityTypes;
                        this.startLogDate = resp.data.startLogDate ? resp.data.startLogDate.split('T')[0] : null;
                        this.calendarMinDate = this.newDate(resp.data.startLogDate);
                        this.dailyCapacity = resp.data.dailyCapacity;
                    })
                    .catch(err => {
                        if (err?.isNetworkError || !err?.response?.status) {
                            return;
                        }
                    });

                if (this.hasBacklogSource) {
                    await Promise.all([
                        this.loadTimeLogSummaryAsync(),
                        this.loadMonthWorkTypeHoursAsync(),
                        this.loadTimeLogItemsByDateAsync(),
                        this.loadCalendarDayProgressAsync()
                    ]);
                }
                await this.loadConfiguredColumns();
                this.detectWorkItemMismatchesAsync();
            } catch (loadErr) {
                console.warn("Timelog initial load encountered an error:", loadErr);
            } finally {
                this.loadingMonthChart = false;
                this.loadingTimeLogSummary = false;
                this.loadingCalendarInfo = false;
                this.loadingTimeLogItems = false;
            }
        },
        methods: {
            loadStoredTimelogFeatureSettings() {
                const defaults = {
                    enabled: true,
                    showStateQuickActions: true,
                    showExternalMismatchBadge: true,
                    showProjectGrouping: true
                };
                try {
                    const raw = localStorage.getItem('vt_timelog_feature_config');
                    if (!raw) return defaults;
                    const parsed = JSON.parse(raw);
                    return {
                        enabled: parsed.enabled !== false,
                        showStateQuickActions: parsed.showStateQuickActions !== false,
                        showExternalMismatchBadge: parsed.showExternalMismatchBadge !== false,
                        showProjectGrouping: parsed.showProjectGrouping !== false
                    };
                } catch (e) {
                    return defaults;
                }
            },
            onTimelogFeatureSettingsSaved(newSettings) {
                if (!newSettings) return;
                this.timelogFeatureSettings = {
                    enabled: newSettings.enabled !== false,
                    showStateQuickActions: newSettings.showStateQuickActions !== false,
                    showExternalMismatchBadge: newSettings.showExternalMismatchBadge !== false,
                    showProjectGrouping: newSettings.showProjectGrouping !== false
                };
            },
            onTimelogEmptyAction(actionKey) {
                if (actionKey === 'add-work') {
                    this.addWorkTimeItem();
                } else if (actionKey === 'add-activity') {
                    this.addActivity();
                } else if (actionKey === 'add-absence') {
                    this.applyAbsenceAsync();
                }
            },
            newDate(dateStr) {
                if (typeof dateStr === "string" && dateStr !== "") {
                    const parts = dateStr.split("T")[0].split("-");
                    if (parts.length === 3)
                        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                    const parsed = new Date(dateStr);
                    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
                }
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), now.getDate());
            },
            async openExportModal() {
                this.$root.$emit('bv::hide::popover');
                this.showExportModal = true;
                if (!this.exportProjectsLoaded) {
                    try {
                        const resp = await this.$http.get('/AccountConfig/Platforms');
                        const options = [];
                        (resp.data || []).forEach(plat => {
                            (plat.organizations || []).forEach(org => {
                                (org.projects || []).forEach(p => {
                                    if (!p.isArchived) {
                                        options.push({ value: p.projectId, text: p.projectName || p.projectId });
                                    }
                                });
                            });
                        });
                        this.exportProjectOptions = options;
                        this.exportProjectsLoaded = true;
                    } catch (e) {
                        console.warn('Could not load projects for export modal:', e);
                    }
                }
            },
            async executeExport() {
                this.exportingLogs = true;
                try {
                    let fromDate = null;
                    let toDate = null;

                    if (this.exportForm.scope === 'month') {
                        const view = this.calendarViewDate || this.newDate();
                        const year = view.getFullYear();
                        const month = view.getMonth();
                        fromDate = format(new Date(year, month, 1), 'yyyy-MM-dd');
                        toDate = format(new Date(year, month + 1, 0), 'yyyy-MM-dd');
                    } else if (this.exportForm.scope === 'picked') {
                        fromDate = this.pickedYMD;
                        toDate = this.pickedYMD;
                    } else if (this.exportForm.scope === 'custom') {
                        fromDate = this.exportForm.fromDate || null;
                        toDate = this.exportForm.toDate || null;
                    }

                    const params = {};
                    if (this.exportForm.projectId) {
                        params.projectId = this.exportForm.projectId;
                    }
                    if (fromDate) {
                        params.fromDate = fromDate;
                    }
                    if (toDate) {
                        params.toDate = toDate;
                    }

                    const resp = await this.$http.get('/TimeManagement/ExportTimeLogs', {
                        params,
                        responseType: 'blob'
                    });

                    const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const dateSuffix = fromDate && toDate ? `${fromDate}_to_${toDate}` : format(new Date(), 'yyyyMMdd');
                    link.setAttribute('download', `time-logs-${dateSuffix}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.toastSuccess('Export Succeeded', 'Your time logs CSV file has been downloaded.');
                    this.showExportModal = false;
                } catch (err) {
                    console.error('Failed to export time logs:', err);
                    this.toastError('Export Failed', 'Could not generate time logs export file.');
                } finally {
                    this.exportingLogs = false;
                }
            },
            async detectWorkItemMismatchesAsync() {
                if (!this.hasBacklogSource || !this.activeProject) {
                    this.mismatchesList = [];
                    return;
                }
                this.loadingMismatches = true;
                try {
                    const params = {};
                    if (this.$store?.state?.lastPickedSprint) {
                        params.sprint = this.$store.state.lastPickedSprint;
                    }
                    const resp = await this.$http.get("/TimeManagement/DetectWorkItemMismatches", { params });
                    this.mismatchesList = resp.data || [];
                } catch (e) {
                    this.mismatchesList = [];
                } finally {
                    this.loadingMismatches = false;
                }
            },
            openReconcileModal() {
                this.$root.$emit('bv::hide::popover');
                if (this.$refs["reconcile-modal"]) {
                    this.$refs["reconcile-modal"].show(null, this.$store?.state?.lastPickedSprint || null);
                }
            },
            async onLogsReconciled({ item, timeLog, createdLogs }) {
                this.mismatchesList = (this.mismatchesList || []).filter(m => m.workItemId !== item.workItemId);
                try {
                    await Promise.all([
                        this.loadTimeLogSummaryAsync(),
                        this.loadMonthWorkTypeHoursAsync(),
                        this.loadCalendarDayProgressAsync()
                    ]);
                    const logsToCheck = createdLogs && createdLogs.length ? createdLogs : (timeLog ? [timeLog] : []);
                    const touchesPicked = logsToCheck.some(l => l && (l.date || l.loggedDate) && String(l.date || l.loggedDate).split('T')[0] === this.pickedYMD);
                    if (touchesPicked) {
                        await this.loadTimeLogItemsByDateAsync();
                    }
                } catch (e) {
                    // Non-blocking UI update
                }
            },
            checkDayChange() {
                const now = this.newDate();
                if (format(now, "yyyy-MM-dd") !== format(this.currentDay, "yyyy-MM-dd")) {
                    this.currentDay = now;
                    this.loadTimeLogSummaryAsync();
                    this.loadMonthWorkTypeHoursAsync();
                }
            },
            startOfMonth(date) {
                return new Date(date.getFullYear(), date.getMonth(), 1);
            },
            clampCalendarViewDate(date) {
                const next = this.startOfMonth(date);
                if (this.startLogDate) {
                    const minMonth = this.startOfMonth(this.newDate(this.startLogDate));
                    if (next < minMonth)
                        return minMonth;
                }
                return next;
            },
            navigateCalendarMonths(delta) {
                const current = this.calendarViewDate || this.newDate(this.pickedYMD);
                const next = this.clampCalendarViewDate(new Date(current.getFullYear(), current.getMonth() + delta, 1));
                this.calendarViewDate = next;
                this.calendarRenderKey += 1;
                this.loadCalendarDayProgressAsync(next);
            },
            navigateCalendarToday() {
                const today = this.newDate();
                this.calendarViewDate = this.startOfMonth(today);
                this.calendarRenderKey += 1;
                this.loadCalendarDayProgressAsync(today);
            },
            isCalendarPrevNavDisabled(delta) {
                if (!this.startLogDate)
                    return false;
                const current = this.calendarViewDate || this.newDate(this.pickedYMD);
                const next = new Date(current.getFullYear(), current.getMonth() + delta, 1);
                return next < this.startOfMonth(this.newDate(this.startLogDate));
            },
            isSelectedDateInFuture: function () {
                return this.isPickedDateFuture;
            },
            removeTimeItem(item) {
                const indexToRemove = _.findIndex(this.timeItems, (i) => i === item);
                if (indexToRemove !== -1)
                    this.timeItems.splice(indexToRemove, 1);
            },
            hasModifyingWorkItems: function () {
                return _.filter(this.timeItemsOfWork, (i) => i.isModifying()).length >= 1;
            },
            hasUnsavedActivityItem: function () {
                return _.some(this.timeItemsOfActivity, (i) => !i.isSaved());
            },
            hasModifyingOtherWorkItems: function (item) {
                return _.filter(this.timeItemsOfWork, (i) => i.vid !== item.vid && i.isModifying()).length >= 1;
            },
            timeRowStateClass(item) {
                if (item.isModifying())
                    return "time-row-modifying";
                if (!item.isSaved())
                    return "time-row-unsaved";
                return "time-row-saved";
            },
            actionHoverPop(title) {
                return {
                    title,
                    container: "body",
                    offset: "0, 8",
                    customClass: "action-hover-pop",
                    boundary: "window"
                };
            },
            actionIconClass(icon) {
                if (!icon) return "bi bi-check";
                if (icon.startsWith("bi ")) return icon;
                return `bi ${icon}`;
            },
            calendarDisabledDates(ymd) {
                if (this.calendarMinDate && ymd < this.calendarMinDate) {
                    return true;
                }
                return false;
            },
            isCurrentWeek(ymd) {
                if (!ymd || !this.currentWeekRange || !this.currentWeekRange.startYMD)
                    return false;
                return ymd >= this.currentWeekRange.startYMD && ymd <= this.currentWeekRange.endYMD;
            },
            calendarDayStatusStyle(ymd) {
                let statusClass = "";
                var theDate = _.find(this.calendarDayStatuses, p => p.date === ymd);
                if (typeof theDate === "object") {
                    if (theDate.style === "absence" || theDate.isAbsence)
                        statusClass = "table-info";
                    else if (theDate.style === "done")
                        statusClass = "table-success";
                    else if (theDate.style === "pending")
                        statusClass = "table-warning";
                    else if (theDate.style === "overflaw")
                        statusClass = "table-danger";
                }
                if (!statusClass && ymd === this.pickedYMD && this.timeItemsOfAbsence && this.timeItemsOfAbsence.length > 0) {
                    statusClass = "table-info";
                }
                if (!statusClass && this.isHoliday(ymd)) {
                    statusClass = "table-info";
                }
                const date = this.newDate(ymd);
                const day = date ? date.getDay() : -1;
                const isWeekend = day === 0 || day === 6;
                if (!statusClass && isWeekend) {
                    statusClass = "bg-light";
                }

                const classes = [];
                if (statusClass) {
                    classes.push(statusClass);
                }
                if (this.isCurrentWeek(ymd)) {
                    classes.push("cal-current-week");
                }
                if (ymd === this.todayYMD) {
                    classes.push("cal-today");
                }
                if (ymd === this.pickedYMD) {
                    classes.push("cal-picked");
                }
                return classes.join(" ");
            },
            stepSelectedDay(delta) {
                try {
                    const currentYMD = this.pickedYMD || this.todayYMD || format(new Date(), "yyyy-MM-dd");
                    const current = this.newDate(currentYMD);
                    current.setDate(current.getDate() + delta);
                    const nextYMD = format(current, "yyyy-MM-dd");
                    if (this.startLogDate && nextYMD < this.startLogDate) {
                        return;
                    }
                    this.onCalendarDayPicked(nextYMD);
                } catch (e) {
                    // fallback directly
                    console.error("stepSelectedDay error:", e);
                }
            },
            pickTodayDay() {
                const targetYMD = this.todayYMD || format(new Date(), "yyyy-MM-dd");
                this.onCalendarDayPicked(targetYMD);
            },
            async onCalendarDayPicked(ymd) {
                if (this.isRevertingCalendarDate)
                    return;
                if (!ymd || ymd === this.pickedYMD)
                    return;

                const previousYMD = this.pickedYMD;
                const canLeave = await this.confirmDiscardUnsavedItems();
                if (!canLeave) {
                    this.isRevertingCalendarDate = true;
                    this.pickedYMD = previousYMD;
                    this.selectedDate = previousYMD ? this.newDate(previousYMD) : this.selectedDate;
                    this.calendarRenderKey += 1;
                    await this.$nextTick();
                    this.isRevertingCalendarDate = false;
                    return;
                }

                if (previousYMD && previousYMD !== ymd) {
                    this.timeItems = this.timeItems.filter(ti => !ti.isModifying());
                    this.persistPendingTimeItems(previousYMD);
                }
                this.pickedYMD = ymd;
                this.selectedDate = this.newDate(ymd);
                this.calendarViewDate = this.startOfMonth(this.newDate(ymd));
                this.calendarRenderKey += 1;
                await this.loadTimeLogItemsByDateAsync();
            },
            async onCalendarContextChange(context) {
                this.contextYear = context.activeDate.getFullYear();
                this.selectedDate = context.selectedDate;
                let prevDate = this.lastActiveYMD ? new Date(this.lastActiveYMD) : null;
                let currDate = context.activeDate;
                const prevYear = prevDate ? prevDate.getFullYear() : null;
                const prevMonth = prevDate ? prevDate.getMonth() : null;
                const currYear = currDate.getFullYear();
                const currMonth = currDate.getMonth();
                //Only trigger on month or year change
                if (
                    prevYear === null || prevMonth === null
                    || prevYear !== currYear || prevMonth !== currMonth
                ) {
                    this.lastActiveYMD = context.activeDate;
                    this.calendarViewDate = this.startOfMonth(context.activeDate);
                    this.loadCalendarDayProgressAsync(context.activeDate);
                }
            },
            getNewTimeItemInstance(logType) {
                let component = this;
                let timeItem = {
                    vid: uuidv4(),
                    workItemId: null,
                    url: "#",
                    referenceUid: null,
                    completed: null,
                    remaining: null,
                    title: "",
                    hours: 0,
                    minutes: 0,
                    totalHours: 0,
                    capacityHours: 0,
                    comments: "",
                    projectId: null,
                    projectName: null,
                    workItemType: "Task",
                    customFields: {},
                    clientAddedAt: Date.now(),

                    assignChanges: function (data) {
                        const prevVid = this.vid;
                        _.assign(this, data);
                        if (!this.vid || this.vid === "00000000-0000-0000-0000-000000000000") {
                            this.vid = this.uid ? `uid-${this.uid}` : (prevVid || uuidv4());
                        }

                        //Split hours and quarter minutes
                        const integerPart = Math.floor(this.hours);
                        const decimalPart = this.hours - integerPart;
                        this.hours = integerPart;
                        this.minutes = decimalPart * 60;

                        if (data && data.customFields) {
                            this.customFields = { ...data.customFields };
                        }

                        this.loadStash();
                    },
                    isWorkItem: function () {
                        return this.workItemId !== null;
                    },
                    isAbsenceItem: function () {
                        return this.referenceUid !== null;
                    },
                    isActivityItem: function () {
                        return this.workItemId === null && this.referenceUid === null;
                    },
                    workTypes: function () {
                        if (this.isWorkItem())
                            return component.loadedWorkTypes;
                        if (this.isAbsenceItem())
                            return component.loadedAbsenceTypes;
                        if (this.isActivityItem())
                            return component.loadedActivityTypes;
                        return [];
                    },
                    getWorkTypeName: function () {
                        return this.logTypeName;
                    },
                    isProgressCompleted: function () {
                        return this.getFullCompletedHours() === this.getFullCapacityHours();
                    },
                    getFullCompletedHours: function () {
                        const effectiveTotal = Math.max(parseFloat(this.totalHours ?? 0), this.getStashedHoursWithMinutes());
                        return effectiveTotal + this.getHoursWithMinutes() - this.getStashedHoursWithMinutes();
                    },
                    getFullCapacityHours: function () {
                        return parseFloat(this.capacityHours ?? 0);
                    },
                    getProgressStyle: function () {
                        const completed = this.getFullCompletedHours();
                        const max = this.getFullCapacityHours();
                        if (completed < max)
                            return "secondary";
                        if (completed === max)
                            return "success";
                        if (completed > max)
                            return "danger";
                    },
                    getMinHours() {
                        if (this.isWorkItem() || this.isActivityItem())
                            return 0;
                        if (this.isAbsenceItem())
                            return component.dailyCapacity;
                    },
                    getMaxHours() {
                        const otherItemSavedHours = component.totalSavedHours - this.getStashedHoursWithMinutes();
                        return component.dailyCapacity - otherItemSavedHours;
                    },
                    getHoursWithMinutes() {
                        return parseFloat(this.hours ?? 0) + (this.minutes ?? 0) / 60;
                    },
                    getStashedHoursWithMinutes() {
                        return parseFloat(this.stashedData.hours ?? 0) + (this.stashedData.minutes ?? 0) / 60;
                    }
                };

                if (logType === this.TIME_ITEM_TYPE_WORK) {
                    if (component.isRoleDev)
                        timeItem.logTypeName = "Development";
                    if (component.isRoleQA)
                        timeItem.logTypeName = "Testing";
                    if (component.isRoleSA)
                        timeItem.logTypeName = "Business Analysis";
                }
                else {
                    timeItem.logTypeName = _.first(timeItem.workTypes());
                }

                component.addChangeResetSupport(timeItem, "uid", ["logTypeName", "hours", "minutes", "capacityHours", "comments", "customFields"]);
                component.addStateSupport(timeItem);

                return timeItem;
            },
            addWorkTimeItem() {
                this.pickWorkItemModal.pickWorkItem(
                    this.getNewTimeItemInstance,
                    (item) => {
                        item.clientAddedAt = Date.now();
                        item.loadStash();
                        this.timeItems.unshift(item);
                        this.shakingWorkItemVid = item.vid;

                        this.$nextTick(() => {
                            if (this.groupedWorkItems && this.groupedWorkItems.length) {
                                const targetGroup = this.groupedWorkItems.find(g => (g.items || []).some(i => i.vid === item.vid));
                                if (targetGroup && this.projectCollapses[targetGroup.index]) {
                                    this.$set(this.projectCollapses, targetGroup.index, false);
                                }
                            }

                            const scrollToRow = () => {
                                const rowEl = document.getElementById(`work-row-${item.vid}`)
                                    || document.querySelector(`[data-test-id="${item.id || item.workItemId || item.vid}"]`)
                                    || (this.$refs.workItemsGrid?.$el || this.$refs.workItemsGrid);
                                if (rowEl && typeof rowEl.scrollIntoView === 'function') {
                                    rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            };

                            requestAnimationFrame(() => {
                                scrollToRow();
                            });

                            setTimeout(() => {
                                scrollToRow();
                            }, 100);

                            setTimeout(() => {
                                const hoursInput = document.getElementById(`wihours-${item.vid}`);
                                if (hoursInput && typeof hoursInput.focus === 'function') {
                                    hoursInput.focus();
                                    if (typeof hoursInput.select === 'function') {
                                        hoursInput.select();
                                    }
                                }
                            }, 400);

                            setTimeout(() => {
                                if (this.shakingWorkItemVid === item.vid) {
                                    this.shakingWorkItemVid = null;
                                }
                            }, 1400);
                        });
                    }
                );
            },
            async applyAbsenceAsync(item) {
                this.$root.$emit('bv::hide::popover');
                if (item instanceof PointerEvent) {
                    item = this.getNewTimeItemInstance(this.TIME_ITEM_TYPE_ABSENCE);
                }
                await this.absenceModal.applyAbsenceFormAsync(
                    item,
                    this.loadedAbsenceTypes,
                    async () => {
                        await this.loadTimeLogItemsByDateAsync(true);
                        await this.loadCalendarDayProgressAsync();
                        await this.loadMonthWorkTypeHoursAsync();
                        await this.loadTimeLogSummaryAsync();
                    },
                    null,
                    this.startLogDate
                );

            },
            addActivity() {
                let activityItem = this.getNewTimeItemInstance(this.TIME_ITEM_TYPE_ACTIVITY);
                activityItem.clientAddedAt = Date.now();
                this.timeItems.unshift(activityItem);
                this.shakingActivityVid = activityItem.vid;

                this.$nextTick(() => {
                    const scrollToRow = () => {
                        const rowEl = document.getElementById(`activity-row-${activityItem.vid}`)
                            || document.querySelector(`[data-test-id="${activityItem.id || activityItem.vid}"]`)
                            || (this.$refs.activityItemsGrid?.$el || this.$refs.activityItemsGrid);
                        if (rowEl && typeof rowEl.scrollIntoView === 'function') {
                            rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    };

                    requestAnimationFrame(() => {
                        scrollToRow();
                    });

                    setTimeout(() => {
                        scrollToRow();
                    }, 100);

                    setTimeout(() => {
                        let commentInput = this.$refs[`actcomment-${activityItem.vid}`];
                        if (Array.isArray(commentInput)) {
                            commentInput = commentInput[0];
                        }
                        if (commentInput) {
                            const el = commentInput.$el || commentInput;
                            if (el && typeof el.focus === 'function') {
                                el.focus();
                            }
                        }
                    }, 350);

                    setTimeout(() => {
                        if (this.shakingActivityVid === activityItem.vid) {
                            this.shakingActivityVid = null;
                        }
                    }, 1400);
                });
            },
            async upsertTimeLogAsync(item, timeLogType) {
                const formData = new FormData();
                formData.append("vid", item.vid);
                const uidNum = parseInt(item.uid);
                if (!isNaN(uidNum)) {
                    formData.append("uid", uidNum);
                }
                const wiIdNum = parseInt(item.workItemId);
                if (!isNaN(wiIdNum)) {
                    formData.append("workItemId", wiIdNum.toString());
                    const remainingHours = item.getFullCapacityHours() - item.getFullCompletedHours();
                    if (!isNaN(remainingHours)) {
                        formData.append("remainingHours", remainingHours.toString());
                    }
                }
                formData.append("date", this.pickedYMD);
                formData.append("logType", timeLogType);
                formData.append("logTypeName", item.logTypeName);
                formData.append("hours", item.getHoursWithMinutes());
                if (item.comments)
                    formData.append("comments", item.comments);
                formData.append("customFieldsJson", JSON.stringify(item.customFields || {}));
                if (item.projectId) {
                    formData.append("projectId", item.projectId);
                }
                if (item.projectName) {
                    formData.append("projectName", item.projectName);
                }
                if (item.teamId) {
                    formData.append("teamId", item.teamId);
                }
                if (item.platformType !== undefined && item.platformType !== null) {
                    formData.append("platformType", item.platformType);
                }
                if (item.organizationId) {
                    formData.append("organizationId", item.organizationId);
                }

                this.loadingTimeLogItems = true;
                await this.$http
                    .post("/TimeManagement/UpsertTimeLog", formData)
                    .then(() => {
                        this.toastSuccess("Saved!");
                    })
                    .catch((err) => {
                        this.handleMutationError(err, "Failed to save work item");
                    })
                    .finally(() => this.loadingTimeLogItems = false);

                await this.loadTimeLogItemsByDateAsync(true);
                await this.loadCalendarDayProgressAsync();
                await this.loadMonthWorkTypeHoursAsync();
                await this.loadTimeLogSummaryAsync();
            },
            toggleProjectCollapse(index) {
                this.$set(this.projectCollapses, index, !this.projectCollapses[index]);
            },
            async setTimeLogStateAsync(item, state) {
                const formData = new FormData();
                const wiIdNum = parseInt(item.workItemId);
                if (!isNaN(wiIdNum)) {
                    formData.append("workItemId", wiIdNum.toString());
                }
                formData.append("state", state);
                if (item.projectId) {
                    formData.append("projectId", item.projectId);
                }
                if (item.completedWork && state.toLowerCase() === this.WI_STATE_INPROGRESS.toLowerCase()) {
                    formData.append("completedWork", item.completedWork);
                }

                this.loadingTimeLogItems = true;
                const params = item.projectId ? { projectId: item.projectId } : {};
                await this.$http
                    .patch("/TimeManagement/ChangeWorkItemState", formData, { params })
                    .then(async () => {
                        item.state = state;
                        this.toastSuccess("Saved!");
                    })
                    .catch((err) => {
                        this.handleMutationError(err, "Failed to change work item state");
                    })
                    .finally(() => this.loadingTimeLogItems = false);

                await this.loadTimeLogItemsByDateAsync(true);
                await this.loadCalendarDayProgressAsync();
                await this.loadMonthWorkTypeHoursAsync();
                await this.loadTimeLogSummaryAsync();
            },
            async deleteTimeLogAsync(item) {
                if (!item.isSaved()) {
                    this.removeTimeItem(item);
                    return;
                }

                let confirmMessage = "This saved item will be permanently removed.";
                if (item.isWorkItem())
                    confirmMessage = "This saved work item will be permanently removed. Logged hours will be subtracted from Completed Hours for the task.";
                else if (item.isActivityItem())
                    confirmMessage = "This saved activity will be permanently removed.";
                else if (item.isAbsenceItem())
                    confirmMessage = "This saved absence will be permanently removed.";

                this.$bvModal
                    .msgBoxConfirm(confirmMessage, {
                        size: "sm",
                        buttonSize: "sm",
                        okTitle: "Remove",
                        cancelTitle: "Cancel",
                        okVariant: "danger",
                        headerClass: "p-2 border-bottom-0",
                        footerClass: "p-2 border-top-0",
                        centered: true,
                        dialogClass: "modal-dialog-confirm"
                    })
                    .then(async (isOk) => {
                        if (!isOk)
                            return;

                        this.loadingTimeLogItems = true;
                        await this.$http.delete("/TimeManagement/DeleteTimeLog/" + item.uid)
                            .then(() => {
                                this.removeTimeItem(item);
                                this.toastSuccess("Deleted!");
                            })
                            .finally(() => this.loadingTimeLogItems = false);

                        await this.loadTimeLogItemsByDateAsync(true);
                        await this.loadCalendarDayProgressAsync();
                        await this.loadMonthWorkTypeHoursAsync();
                        await this.loadTimeLogSummaryAsync();
                    });
            },
            async loadTimeLogItemsByDateAsync(preservePending = false) {
                this.isHydratingTimeItems = true;
                this.loadingTimeLogItems = true;
                if (preservePending)
                    this.persistPendingTimeItems(this.pickedYMD);

                await this.$http.get("/TimeManagement/ListTimeLogByDate", { params: { date: this.pickedYMD } })
                    .then((resp) => {
                        const pendingRaw = (this.$store.state.pendingTimeItemsByDate || {})[this.pickedYMD] || [];
                        let merged = pendingRaw.map(p => this.hydrateTimeItem(p));

                        _.forEach(resp.data, rItem => {
                            let item = this.hydrateTimeItem(rItem);
                            var matchIndex = _.findIndex(merged, (ti) => (ti.uid && item.uid && ti.uid === item.uid) || (ti.vid && item.vid && ti.vid !== "00000000-0000-0000-0000-000000000000" && ti.vid === item.vid));
                            if (matchIndex === -1)
                                merged.push(item);
                            else {
                                item.clientAddedAt = merged[matchIndex].clientAddedAt || item.clientAddedAt;
                                merged.splice(matchIndex, 1, item);
                            }
                        });

                        this.timeItems = merged;
                        this.lastPickedYMD = this.pickedYMD;
                        this.$store.commit("setLastPickedCalendarDate", this.pickedYMD);
                        this.persistClientAddedAtMap(merged);

                        const projectIds = _.uniq(
                            merged
                                .map(i => i.projectId || i.projectName)
                                .filter(pid => pid && typeof pid === "string" && pid.trim() !== "")
                        );
                        if (projectIds.length > 0) {
                            this.ensureWorkflowsAsync(projectIds);
                        } else {
                            this.ensureWorkflowsAsync(["default"]);
                        }
                    })
                    .catch((err) => {
                        if (err?.isNetworkError || !err?.response?.status) {
                            return;
                        }
                    })
                    .finally(() => {
                        this.loadingTimeLogItems = false;
                        this.isHydratingTimeItems = false;
                        this.persistPendingTimeItems(this.pickedYMD);
                    });
            },
            async loadTimeLogSummaryAsync() {
                this.loadingTimeLogSummary = true;
                await this.$http.get("/TimeManagement/GetPickedDateSummary", { params: { date: this.todayYMD } })
                    .then((resp) => {
                        this.todayLoggedHours = Number(resp.data.todayLoggedHours) || 0;
                        this.thisWeekLoggedHours = resp.data.thisWeekLoggedHours;
                        this.thisWeekToDateHours = Number(resp.data.thisWeekToDateHours) || 0;
                        this.thisWeekFutureHours = Number(resp.data.thisWeekFutureHours) || 0;
                        this.thisWeekTotalHours = resp.data.thisWeekTotalHours;
                        this.thisMonthLoggedHours = resp.data.thisMonthLoggedHours;
                        this.thisMonthToDateHours = Number(resp.data.thisMonthToDateHours) || 0;
                        this.thisMonthFutureHours = Number(resp.data.thisMonthFutureHours) || 0;
                        this.thisMonthTotalHours = resp.data.thisMonthTotalHours;
                        if (this.isCalendarCurrentMonthDisplayed && resp.data.thisMonthByWorkTypeHours) {
                            this.monthWorkTypeHours = resp.data.thisMonthByWorkTypeHours;
                        }
                    })
                    .catch((err) => {
                        if (err?.isNetworkError || !err?.response?.status) {
                            return;
                        }
                    })
                    .finally(() => this.loadingTimeLogSummary = false);
            },
            async loadMonthWorkTypeHoursAsync(targetDate) {
                const dateObj = targetDate
                    ? (targetDate instanceof Date ? targetDate : this.newDate(targetDate))
                    : (this.calendarViewDate || this.newDate(this.pickedYMD) || this.todayDate);
                const queryDate = format(dateObj, "yyyy-MM-dd");
                this.monthWorkTypeRequestId = (this.monthWorkTypeRequestId || 0) + 1;
                const currentRequestId = this.monthWorkTypeRequestId;
                this.loadingMonthChart = true;
                await this.$http.get("/TimeManagement/GetMonthWorkTypeHours", { params: { date: queryDate } })
                    .then((resp) => {
                        if (currentRequestId === this.monthWorkTypeRequestId) {
                            this.monthWorkTypeHours = resp.data || [];
                        }
                    })
                    .catch((err) => {
                        if (err?.isNetworkError || !err?.response?.status) {
                            return;
                        }
                    })
                    .finally(() => {
                        if (currentRequestId === this.monthWorkTypeRequestId) {
                            this.loadingMonthChart = false;
                        }
                    });
            },
            async loadCalendarDayProgressAsync(date) {
                const isFromCalendarEvent = typeof date !== "undefined";
                if (!isFromCalendarEvent) {
                    date = this.newDate(this.pickedYMD);
                }
                let startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
                let minDate = new Date(startOfMonth);
                let maxDate = new Date(startOfMonth);
                minDate.setDate(startOfMonth.getDate() - 31);
                maxDate.setDate(startOfMonth.getDate() + 62);

                let endpointParams = { start: format(minDate, "yyyy-MM-dd"), end: format(maxDate, "yyyy-MM-dd") };
                let isSameParams =
                    this.lastLoadedDayProgressParams.start === endpointParams.start
                    || this.lastLoadedDayProgressParams.end === endpointParams.end;
                if (isFromCalendarEvent && isSameParams)
                    return;

                this.loadingCalendarInfo = true;
                await this.$http.get("/TimeManagement/GetPickedRangeDayStatuses", { params: endpointParams })
                    .then((resp) => {
                        this.calendarDayStatuses = resp.data.calendarDayStatuses;
                        this.lastLoadedDayProgressParams = endpointParams; this.calendarRenderKey += 1;
                    })
                    .catch((err) => {
                        if (err?.isNetworkError || !err?.response?.status) {
                            return;
                        }
                    })
                    .finally(() => this.loadingCalendarInfo = false);
            },
            hasUnsavedTimeItems() {
                return _.some(this.timeItems, ti => ti.isModifying());
            },
            confirmDiscardUnsavedItems() {
                if (!this.hasUnsavedTimeItems())
                    return Promise.resolve(true);
                return this.$bvModal
                    .msgBoxConfirm("There are unsaved items!\rDo you want to discard them?", {
                        size: "sm",
                        buttonSize: "sm",
                        okVariant: "success",
                        headerClass: "p-2 border-bottom-0",
                        footerClass: "p-2 border-top-0",
                        centered: true,
                        dialogClass: "modal-dialog-confirm"
                    })
                    .then(isOk => !!isOk);
            },
            checkChangedItemsBeforeLeave(next) {
                this.confirmDiscardUnsavedItems().then(canLeave => {
                    if (canLeave)
                        next();
                    else
                        next(false);
                });
            },
            copyToClip(text) {
                const el = document.createElement('textarea');
                el.value = text;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                this.toastSuccess("Copied!");
            },
            isHoliday(dateYMD) {
                const comp = this.publicHolidayComponent || this.$refs?.["public-holiday-component"];
                if (!comp || typeof comp.isPublicHoliday !== "function")
                    return false;
                return comp.isPublicHoliday(dateYMD);
            },
            hasAnyWorkTitleOverflow() {
                return (this.timeItemsOfWork || []).some(item => this.expandedWorkTitles[item.vid] || this.isWorkTitleOverflowing(item));
            },
            isWorkTitleOverflowing(item) {
                const refKey = `titleText-${item.vid}`;
                let el = this.$refs[refKey];
                if (Array.isArray(el))
                    el = el[0];
                const measureEl = el && el.parentElement
                    ? el.parentElement.querySelector(".title-text-measure")
                    : null;
                if (measureEl && typeof measureEl.scrollHeight === "number") {
                    const lineHeight = parseFloat(getComputedStyle(measureEl).lineHeight) || 18;
                    return measureEl.scrollHeight > (lineHeight * 3) + 1;
                }
                return !!(item.title && item.title.length > 120);
            },
            onTimeManagerWindowResize() {
                this.resizeAllActivityComments();
            },
            resizeAllActivityComments() {
                this.$nextTick(() => {
                    (this.timeItemsOfActivity || []).forEach(item => this.resizeActivityComment(item.vid));
                });
            },
            resizeActivityComment(vid) {
                this.$nextTick(() => {
                    let textarea = this.$refs[`actcomment-${vid}`];
                    if (Array.isArray(textarea))
                        textarea = textarea[0];
                    const el = textarea && textarea.$el ? textarea.$el : textarea;
                    if (!el || typeof el.scrollHeight !== "number")
                        return;
                    el.style.height = "auto";
                    el.style.height = `${el.scrollHeight + 4}px`;
                });
            },
            toggleWorkTitle(vid) {
                this.$set(this.expandedWorkTitles, vid, !this.expandedWorkTitles[vid]);
            },
            serializeTimeItem(item) {
                return _.pick(item, [
                    "vid", "uid", "workItemId", "url", "referenceUid", "completed", "remaining",
                    "title", "hours", "minutes", "totalHours", "capacityHours",
                    "comments", "logTypeName", "projectId", "projectName",
                    "workItemType", "workItemTypeIcon", "state", "clientAddedAt", "customFields"
                ]);
            },
            hydrateTimeItem(data) {
                const isWork = data.workItemId !== null && typeof data.workItemId !== "undefined";
                const isAbsence = data.referenceUid !== null && typeof data.referenceUid !== "undefined";
                const type = isWork ? this.TIME_ITEM_TYPE_WORK : (isAbsence ? this.TIME_ITEM_TYPE_ABSENCE : this.TIME_ITEM_TYPE_ACTIVITY);
                let item = this.getNewTimeItemInstance(type);
                const createdAt = item.clientAddedAt;
                item.assignChanges(data);
                const storedAddedAt = (this.$store.state.timeItemClientAddedAt || {})[item.vid];
                item.clientAddedAt = data.clientAddedAt || storedAddedAt || (item.isSaved() ? 0 : createdAt);
                return item;
            },
            persistPendingTimeItems(dateYMD) {
                if (!dateYMD)
                    return;
                const pending = this.timeItems
                    .filter(i => !i.isSaved() && (i.isWorkItem() || i.isActivityItem()))
                    .map(i => this.serializeTimeItem(i));
                this.$store.commit("setPendingTimeItemsForDate", { date: dateYMD, items: pending });
                this.persistClientAddedAtMap(this.timeItems);
            },
            persistClientAddedAtMap(items) {
                _.forEach(items, i => {
                    if (i.vid && i.clientAddedAt && (i.isWorkItem() || i.isActivityItem()))
                        this.$store.commit("setTimeItemClientAddedAt", { vid: i.vid, addedAt: i.clientAddedAt });
                });
            },
            openColumnSettings(initialSearchField = '') {
                const field = typeof initialSearchField === 'string' ? initialSearchField : '';
                if (this.$refs.modalTimeColumns) {
                    this.$refs.modalTimeColumns.showModal(field);
                }
            },
            getCustomFieldAllowedValues(col) {
                if (!col) return [];
                if (col.allowedValues && col.allowedValues.length > 0) {
                    return col.allowedValues;
                }
                if (col.referenceName && this.discoveredCustomFields && this.discoveredCustomFields.length > 0) {
                    const match = this.discoveredCustomFields.find(d => d.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                    if (match && match.allowedValues && match.allowedValues.length > 0) {
                        return match.allowedValues;
                    }
                }
                const fieldType = (col.fieldType || '').toLowerCase();
                if (fieldType === 'boolean') {
                    return ['Yes', 'No'];
                }
                return [];
            },
            async onTimeColumnsSaved(newColumns) {
                this.activeTimeGridColumns = this.normalizeGridColumns(newColumns);
                await this.enrichColumnsWithAllowedValues();
            },
            async saveTimeGridColumnsSilentAsync() {
                try {
                    const payload = (this.activeTimeGridColumns || []).map((c, index) => ({
                        referenceName: c.referenceName,
                        label: c.label,
                        fieldType: c.fieldType || '',
                        metricSuffix: c.metricSuffix || '',
                        width: c.width || '',
                        isSystem: !!c.isSystem,
                        allowedValues: c.allowedValues || [],
                        valueColors: c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {},
                        workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                        order: index + 1,
                        isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                        hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                    }));
                    await this.$http.post("/TimeManagement/SaveTimeGridColumns", payload);
                } catch (err) {
                    console.warn("Could not persist resized time grid column width:", err);
                }
            },
            onWorkColumnResize({ referenceName, width, lockIfUnset }) {
                if (!referenceName || !width) return;
                const target = (this.activeTimeGridColumns || []).find(c => c.referenceName === referenceName);
                if (!target) return;
                const hasCustomPx = target.width && /^\d+(\.\d+)?px$/i.test(String(target.width).trim());
                if (lockIfUnset && hasCustomPx) return;
                if (target.width !== width) {
                    this.$set(target, 'width', width);
                }
            },
            onWorkColumnResizeEnd() {
                this.saveTimeGridColumnsSilentAsync();
            },
            onWorkColumnResizeReset({ referenceName }) {
                if (!referenceName) return;
                const target = (this.activeTimeGridColumns || []).find(c => c.referenceName === referenceName);
                if (target && target.width) {
                    this.$set(target, 'width', '');
                    this.saveTimeGridColumnsSilentAsync();
                }
            },
            autoFitTimeGridColumns() {
                const cols = this.activeTimeGridColumns || [];
                if (!cols.length) return;
                const items = this.timeItemsOfWork || [];

                const customAllocations = [];
                for (const col of cols) {
                    const isVis = col.isVisible !== undefined ? !!col.isVisible : !col.hidden;
                    if (!isVis || col.isSystem || col.referenceName === 'System.Title' || col.referenceName === 'System.Action') {
                        this.$set(col, 'width', '');
                        continue;
                    }
                    const labelLen = (col.label || '').trim().length;
                    const labelPx = Math.round(labelLen * 6.5 + 22);
                    let idealPx;
                    let minPx;
                    if (this.isBooleanColumn(col)) {
                        minPx = 76;
                        idealPx = Math.max(84, Math.min(116, labelPx));
                    } else if (this.isNumericColumn(col)) {
                        const suffixLen = (col.metricSuffix || '').trim().length;
                        const controlPx = suffixLen > 0 ? (82 + suffixLen * 7) : 78;
                        minPx = suffixLen > 0 ? 84 : 74;
                        idealPx = Math.max(88, Math.min(130, Math.max(controlPx, Math.min(labelPx, 124))));
                    } else {
                        minPx = 88;
                        let maxValLen = 0;
                        for (const item of items) {
                            if (!this.isColumnSupportedForWorkItem(col, item)) continue;
                            const val = this.getCustomFieldValue(item, col.referenceName);
                            if (val !== null && val !== undefined) {
                                const len = String(val).trim().length;
                                if (len > maxValLen) maxValLen = len;
                            }
                        }
                        const contentPx = maxValLen > 0 ? Math.round(maxValLen * 6.8 + 36) : 96;
                        idealPx = Math.max(96, Math.min(172, Math.max(contentPx, Math.min(labelPx, 144))));
                    }
                    customAllocations.push({ col, idealPx, minPx });
                }

                if (customAllocations.length > 0) {
                    const gridEl = this.$refs.workGridShell && this.$refs.workGridShell.$el
                        ? (this.$refs.workGridShell.$el.querySelector('.grid-shell-scroll') || this.$refs.workGridShell.$el)
                        : null;
                    const containerW = (gridEl && gridEl.clientWidth > 320) ? gridEl.clientWidth : (typeof window !== 'undefined' ? Math.max(960, window.innerWidth - 380) : 1100);
                    const visibleCols = cols.filter(c => c.isVisible !== undefined ? !!c.isVisible : !c.hidden);
                    const totalColCount = visibleCols.length;
                    const gapTotalPx = Math.max(0, totalColCount - 1) * 4 + 20;

                    let fixedSystemPx = 0;
                    for (const col of visibleCols) {
                        const ref = col.referenceName;
                        if (ref === 'System.WorkItem') fixedSystemPx += 88;
                        else if (ref === 'System.Title') fixedSystemPx += 220;
                        else if (ref === 'System.State') fixedSystemPx += 116;
                        else if (ref === 'System.Progress') fixedSystemPx += 144;
                        else if (ref === 'System.WorkType') fixedSystemPx += 120;
                        else if (ref === 'System.Hours') fixedSystemPx += 196;
                        else if (ref === 'System.Capacity') fixedSystemPx += 104;
                        else if (ref === 'System.Action') fixedSystemPx += 136;
                    }

                    const availableForCustom = containerW - fixedSystemPx - gapTotalPx;
                    const totalIdealCustom = customAllocations.reduce((sum, a) => sum + a.idealPx, 0);

                    if (totalIdealCustom > availableForCustom && availableForCustom > 0) {
                        const ratio = availableForCustom / totalIdealCustom;
                        for (const a of customAllocations) {
                            const scaled = Math.max(a.minPx, Math.floor(a.idealPx * ratio));
                            this.$set(a.col, 'width', `${scaled}px`);
                        }
                    } else {
                        for (const a of customAllocations) {
                            this.$set(a.col, 'width', `${a.idealPx}px`);
                        }
                    }
                }

                if (this.$refs.workGridShell && typeof this.$refs.workGridShell.resetAllColumnWidths === 'function') {
                    this.$refs.workGridShell.resetAllColumnWidths();
                }
                this.saveTimeGridColumnsSilentAsync();
            },
            getWorkItemType(item) {
                if (!item) return '';
                return item.workItemType || item.type || '';
            },
            isColumnSupportedForWorkItem(col, item) {
                if (!col) return false;
                if (col.isSystem) return true;
                if (item && typeof item.isWorkItem === 'function' && !item.isWorkItem()) {
                    return false;
                }
                const rawType = this.getWorkItemType(item);
                const itemType = String(rawType || '').trim().toLowerCase();

                let supportedTypes = col.workItemTypes;
                if ((!supportedTypes || !supportedTypes.length) && this.discoveredCustomFields && this.discoveredCustomFields.length) {
                    const df = this.discoveredCustomFields.find(f => f.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                    if (df && df.workItemTypes && df.workItemTypes.length) {
                        supportedTypes = df.workItemTypes;
                    }
                }

                if (!supportedTypes || !supportedTypes.length) {
                    return true;
                }

                if (!itemType) {
                    return false;
                }
                return supportedTypes.some(t => String(t || '').trim().toLowerCase() === itemType);
            },
            normalizeGridColumns(cols) {
                if (!cols || !Array.isArray(cols) || cols.length === 0) {
                    return DEFAULT_TIME_GRID_COLUMNS.map(c => ({ ...c, valueColors: c.referenceName === 'System.State' ? getStoredStateColors() : (c.valueColors ? { ...c.valueColors } : {}) }));
                }
                let list = cols
                    .filter(c => c && c.referenceName && c.referenceName.toLowerCase() !== 'system.stateaction')
                    .map((c, idx) => ({
                        referenceName: c.referenceName,
                        label: (c.referenceName === 'System.Action' && (!c.label || c.label === 'Action')) ? 'Actions' : (c.label || c.referenceName),
                        isSystem: c.referenceName.startsWith('System.') || !!c.isSystem,
                        isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                        hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false,
                        allowedValues: c.allowedValues && c.allowedValues.length ? [...c.allowedValues] : (c.referenceName === 'System.State' ? [...DEFAULT_STATE_VALUES] : []),
                        valueColors: c.referenceName === 'System.State'
                            ? { ...getStoredStateColors(), ...(c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {}) }
                            : (c.valueColors && typeof c.valueColors === 'object' ? { ...c.valueColors } : {}),
                        workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                        fieldType: c.fieldType || '',
                        metricSuffix: c.metricSuffix || this.inferMetricSuffix(c),
                        width: c.width || '',
                        order: c.order || idx + 1
                    }));
                DEFAULT_TIME_GRID_COLUMNS.forEach(sysCol => {
                    const existing = list.find(c => c.referenceName.toLowerCase() === sysCol.referenceName.toLowerCase());
                    if (existing) {
                        existing.isSystem = true;
                        if (!existing.label || (existing.referenceName === 'System.Action' && existing.label === 'Action')) {
                            existing.label = sysCol.label;
                        }
                        if ((!existing.allowedValues || !existing.allowedValues.length) && sysCol.allowedValues) {
                            existing.allowedValues = [...sysCol.allowedValues];
                        }
                        if ((!existing.valueColors || Object.keys(existing.valueColors).length === 0) && sysCol.valueColors) {
                            existing.valueColors = { ...sysCol.valueColors };
                        } else if (existing.referenceName === 'System.State') {
                            existing.valueColors = { ...getStoredStateColors(), ...(existing.valueColors || {}) };
                            try {
                                setStoredStateColors(existing.valueColors);
                            } catch (e) {
                                // ignore
                            }
                        }
                    } else {
                        list.push({
                            ...sysCol,
                            allowedValues: sysCol.allowedValues ? [...sysCol.allowedValues] : [],
                            valueColors: sysCol.valueColors ? { ...sysCol.valueColors } : {},
                            workItemTypes: [],
                            metricSuffix: '',
                            order: list.length + 1
                        });
                    }
                });
                list.sort((a, b) => (a.order || 0) - (b.order || 0));
                const actionIdx = list.findIndex(c => c.referenceName === 'System.Action');
                if (actionIdx !== -1 && actionIdx !== list.length - 1) {
                    const actionCol = list.splice(actionIdx, 1)[0];
                    list.push(actionCol);
                }
                return list.map((c, i) => ({
                    ...c,
                    allowedValues: c.allowedValues || [],
                    valueColors: c.valueColors && typeof c.valueColors === 'object' ? { ...c.valueColors } : {},
                    workItemTypes: c.workItemTypes || [],
                    fieldType: c.fieldType || '',
                    metricSuffix: c.metricSuffix || this.inferMetricSuffix(c),
                    width: c.width || '',
                    order: i + 1
                }));
            },
            async enrichColumnsWithAllowedValues() {
                try {
                    const resp = await this.$http.get("/TaskManagement/DiscoverCustomFields");
                    const discovered = resp.data || [];
                    this.discoveredCustomFields = discovered;
                    (this.activeTimeGridColumns || []).forEach(c => {
                        const match = discovered.find(d => d.referenceName.toLowerCase() === c.referenceName.toLowerCase());
                        if (match && match.allowedValues && match.allowedValues.length > 0) {
                            this.$set(c, 'allowedValues', [...match.allowedValues]);
                        } else if (match && (match.fieldType || '').toLowerCase() === 'boolean') {
                            this.$set(c, 'allowedValues', ['Yes', 'No']);
                        }
                        if (match && match.workItemTypes && match.workItemTypes.length > 0 && (!c.workItemTypes || !c.workItemTypes.length)) {
                            this.$set(c, 'workItemTypes', [...match.workItemTypes]);
                        }
                        if (match && match.fieldType && !c.fieldType) {
                            this.$set(c, 'fieldType', match.fieldType);
                        }
                        const inferredSuffix = (match && match.metricSuffix) || this.inferMetricSuffix({ ...c, fieldType: (match && match.fieldType) || c.fieldType });
                        if (inferredSuffix && !c.metricSuffix) {
                            this.$set(c, 'metricSuffix', inferredSuffix);
                        }
                        if (match && match.metricSuffix && !c.metricSuffix) {
                            this.$set(c, 'metricSuffix', match.metricSuffix);
                        }
                    });
                    this.activeTimeGridColumns = [...this.activeTimeGridColumns];
                } catch (err) {
                    console.warn("Could not enrich columns with custom field allowed values:", err);
                }
            },
            async loadConfiguredColumns() {
                this.loadingTimeGridColumns = true;
                try {
                    const resp = await this.$http.get("/TimeManagement/GetTimeGridColumns");
                    if (resp && resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
                        this.activeTimeGridColumns = this.normalizeGridColumns(resp.data);
                    } else {
                        this.activeTimeGridColumns = this.normalizeGridColumns([]);
                    }
                    await this.enrichColumnsWithAllowedValues();
                } catch (err) {
                    console.warn("Could not load time grid columns, using defaults:", err);
                    this.activeTimeGridColumns = this.normalizeGridColumns([]);
                } finally {
                    this.loadingTimeGridColumns = false;
                }
            },
            getCustomFieldValue(item, referenceName) {
                if (!item) return '';
                if (item.customFields && referenceName) {
                    const lowerRef = String(referenceName).toLowerCase();
                    const entry = Object.entries(item.customFields).find(([k]) => String(k).toLowerCase() === lowerRef);
                    if (entry && entry[1] !== undefined && entry[1] !== null && item.customFields[referenceName] === undefined) {
                        return entry[1];
                    }
                }
                if (item.customFields && item.customFields[referenceName] !== undefined && item.customFields[referenceName] !== null) {
                    return item.customFields[referenceName];
                }
                return '';
            },
            setCustomFieldValue(item, referenceName, value) {
                if (!item) return;
                if (!item.customFields) {
                    this.$set(item, 'customFields', {});
                }
                this.$set(item.customFields, referenceName, value);
            },
            getStateColor(col, state) {
                if (!col || !state) return null;
                let hex = null;
                if (col.valueColors && typeof col.valueColors === 'object') {
                    hex = col.valueColors[state] || col.valueColors[String(state).trim()];
                    if (!hex) {
                        const entry = Object.entries(col.valueColors).find(([k]) => k.toLowerCase() === String(state).trim().toLowerCase());
                        if (entry) hex = entry[1];
                    }
                }
                if (!hex && col.referenceName === 'System.State') {
                    const normVal = String(state).trim().toLowerCase();
                    const storedColors = getStoredStateColors();
                    const defEntry = Object.entries(storedColors).find(([k]) => k.toLowerCase() === normVal);
                    if (defEntry) {
                        hex = defEntry[1];
                    } else if (normVal === 'to do' || normVal === 'todo' || normVal === 'proposed' || normVal === 'design') {
                        hex = col.valueColors?.['New'] || col.valueColors?.['To Do'] || storedColors['New'] || storedColors['To Do'] || DEFAULT_STATE_COLORS['To Do'];
                    } else if (normVal === 'in progress' || normVal === 'inprogress' || normVal === 'active' || normVal === 'doing' || normVal === 'committed' || normVal === 'started') {
                        hex = col.valueColors?.['Active'] || col.valueColors?.['In Progress'] || storedColors['Active'] || storedColors['In Progress'] || DEFAULT_STATE_COLORS['In Progress'];
                    } else if (normVal === 'done' || normVal === 'resolved' || normVal === 'closed' || normVal === 'completed') {
                        hex = col.valueColors?.['Done'] || col.valueColors?.['Resolved'] || col.valueColors?.['Closed'] || storedColors['Done'] || storedColors['Resolved'] || storedColors['Closed'] || DEFAULT_STATE_COLORS['Done'];
                    } else if (normVal === 'removed' || normVal === 'cut' || normVal === 'abandoned') {
                        hex = col.valueColors?.['Removed'] || storedColors['Removed'] || DEFAULT_STATE_COLORS['Removed'];
                    }
                }
                return hex || null;
            },
            getValueColorSelectStyle(col, val) {
                const hex = this.getStateColor(col, val);
                if (!hex) return null;
                return {
                    '--val-color': hex
                };
            },
            columnFieldType(col) {
                if (!col) return '';
                const match = col.referenceName && (this.discoveredCustomFields || []).find(d => d.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                return ((match && match.fieldType) || col.fieldType || '').toLowerCase();
            },
            inferMetricSuffix(col) {
                if (!col) return '';
                if (col.allowedValues && col.allowedValues.length > 0) return '';
                const ft = this.columnFieldType(col);
                if (ft === 'boolean' || ft === 'identity') return '';
                const combined = `${col.referenceName || ''} ${col.label || ''}`.toLowerCase();
                if (combined.includes('time') || combined.includes('hour') || combined.includes('hrs') || combined.includes('duration') || combined.includes('effort') || combined.includes('estimate')) {
                    return 'Hrs';
                }
                if (combined.includes('min') || combined.includes('minute')) {
                    return 'Min';
                }
                if (combined.includes('point') || combined.includes('pts')) {
                    return 'Pts';
                }
                if (combined.includes('percent') || combined.includes('pct')) {
                    return '%';
                }
                return '';
            },
            getColumnMetricSuffix(col) {
                if (!col) return '';
                return col.metricSuffix || this.inferMetricSuffix(col);
            },
            isBooleanColumn(col) {
                return this.columnFieldType(col) === 'boolean';
            },
            isIntegerColumn(col) {
                const ft = this.columnFieldType(col);
                return ft === 'integer' || ft === 'picklistinteger' || ft === 'int';
            },
            stepCustomField(item, col, delta) {
                if (!item || !col || this.hasModifyingOtherWorkItems(item)) return;
                const current = parseFloat(this.getCustomFieldValue(item, col.referenceName)) || 0;
                const next = Math.max(0, Math.round((current + delta) * 100) / 100);
                this.setCustomFieldValue(item, col.referenceName, String(next));
            },
            isNumericColumn(col) {
                if (!col) return false;
                const ft = this.columnFieldType(col);
                if (/^(picklist)?(integer|double|decimal|number|int|float)$/.test(ft)) return true;
                if (ft === 'boolean' || ft === 'identity') return false;
                const combined = `${col.referenceName || ''} ${col.label || ''}`.toLowerCase();
                if (combined.includes('time') || combined.includes('hour') || combined.includes('hrs') || combined.includes('duration') || combined.includes('saved')) return true;
                if (!col) return false;
                const type = (col.fieldType || '').toLowerCase();
                if (type === 'integer' || type === 'double' || type === 'decimal') return true;
                const ref = (col.referenceName || '').toLowerCase();
                return ref.includes('point') || ref.includes('effort') || ref.includes('estimate');
            },
            onCustomFieldKeydown(event, col) {
                if (!this.isNumericColumn(col)) return;
                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', '.', '-'];
                if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
                if (!/^[0-9]$/.test(event.key)) {
                    event.preventDefault();
                }
            },
            handleMutationError(err, defaultTitle, defaultMsg) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error(defaultTitle, err);

                const data = err?.response?.data;
                let fieldName = '';
                let ruleMessage = '';

                if (data?.isRuleValidationError) {
                    fieldName = data.fieldName || data.fieldReferenceName || '';
                    ruleMessage = data.ruleMessage || '';
                } else {
                    const rawError = (typeof data === 'string' ? data : null) ||
                        (data?.errors && Array.isArray(data.errors) ? data.errors.join(' ') : null) ||
                        data?.message ||
                        err?.message || '';

                    const isRuleError = rawError.includes('TF401320') ||
                        rawError.includes('Rule validation failed') ||
                        rawError.includes('RequiredField') ||
                        rawError.includes('cannot be empty');

                    if (isRuleError) {
                        const match = rawError.match(/field\s+['"]([^'"]+)['"]/i) ||
                            rawError.match(/Field\s+['"]([^'"]+)['"]\s+cannot be empty/i);
                        fieldName = match ? match[1].trim() : '';
                        const display = fieldName ? `'${fieldName}'` : 'a required field';
                        ruleMessage = `Azure DevOps process rules require a value for ${display}. Add this column to your grid in VelocyTrack to provide a value.`;
                    }
                }

                if (ruleMessage || fieldName) {
                    this.toastRuleValidation(
                        fieldName,
                        ruleMessage,
                        () => this.openColumnSettings(fieldName),
                        10
                    );
                    return;
                }

                const errMsg = (data?.errors && data.errors[0]) || data?.message || defaultMsg || 'An error occurred during save.';
                this.toastError(defaultTitle, errMsg);
            },
            stepHours(item, delta) {
                if (item.isWorkItem && item.isWorkItem() && this.hasModifyingOtherWorkItems(item)) return;
                const current = parseInt(item.hours, 10) || 0;
                const min = item.getMinHours ? (item.getMinHours() || 0) : 0;
                const next = Math.max(min, current + delta);
                item.hours = next;
            },
            stepMinutes(item, delta) {
                if (item.isWorkItem && item.isWorkItem() && this.hasModifyingOtherWorkItems(item)) return;
                const current = parseInt(item.minutes, 10) || 0;
                const next = Math.max(0, Math.min(45, current + delta));
                item.minutes = next;
            },
            stepCapacity(item, delta) {
                if (item.isWorkItem && item.isWorkItem() && this.hasModifyingOtherWorkItems(item)) return;
                const current = parseInt(item.capacityHours, 10) || 0;
                const next = Math.max(0, current + delta);
                item.capacityHours = next;
            }
        },
        beforeRouteLeave(to, from, next) {
            this.checkChangedItemsBeforeLeave(next);
        }
    };
</script>

<style scoped>
    .time-management-page {
        padding-bottom: 8rem;
        margin-bottom: 4rem;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-caption {
        visibility: hidden !important;
        min-height: 36px !important;
        height: 36px !important;
        margin-bottom: 0.25rem !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-nav {
        display: none !important;
    }

    /* Current Week Highlight (Row Band) */
    .time-manager-calendar ::v-deep .b-calendar-grid-body .row:has(.cal-current-week) {
        position: relative;
        border-radius: 8px;
        background: linear-gradient(90deg, rgba(2, 132, 199, 0.09) 0%, rgba(2, 132, 199, 0.04) 70%, rgba(2, 132, 199, 0.02) 100%);
        border: 1px solid rgba(2, 132, 199, 0.18);
        margin-top: 1px;
        margin-bottom: 1px;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .row:has(.cal-current-week) {
        background: linear-gradient(90deg, rgba(56, 189, 248, 0.12) 0%, rgba(56, 189, 248, 0.05) 70%, rgba(56, 189, 248, 0.02) 100%);
        border-color: rgba(56, 189, 248, 0.25);
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .row:has(.cal-current-week) > .col:first-child {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .row:has(.cal-current-week) > .col:last-child {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-current-week:not(.bg-light):not(.table-success):not(.table-warning):not(.table-danger):not(.table-info) {
        background-color: rgba(2, 132, 199, 0.05);
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-current-week:not(.bg-light):not(.table-success):not(.table-warning):not(.table-danger):not(.table-info) {
        background-color: rgba(56, 189, 248, 0.06);
    }

    /* Day status styles inside calendar */
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-success {
        background-color: #c3e6cb !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-warning {
        background-color: #ffeeba !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-danger {
        background-color: #f5c6cb !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-info {
        background-color: #bee5eb !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-success {
        background-color: rgba(22, 163, 74, 0.3) !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-warning {
        background-color: rgba(217, 119, 6, 0.3) !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-danger {
        background-color: rgba(220, 38, 38, 0.3) !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.table-info {
        background-color: rgba(2, 132, 199, 0.3) !important;
    }

    /* Today Highlight (Current Day) */
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn {
        position: relative;
        box-shadow: 0 0 0 2px #0284c7, 0 0 8px rgba(2, 132, 199, 0.4) !important;
        font-weight: 800 !important;
        opacity: 1 !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn {
        box-shadow: 0 0 0 2px #38bdf8, 0 0 10px rgba(56, 189, 248, 0.55) !important;
        opacity: 1 !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn::after {
        content: '';
        position: absolute;
        bottom: 2px;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: #0284c7;
        box-shadow: 0 0 3px rgba(2, 132, 199, 0.5);
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn::after {
        background-color: #38bdf8;
        box-shadow: 0 0 4px #38bdf8;
    }

    /* Picked / Selected Day (persists even when calendar loses focus) */
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-picked .btn,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col[aria-selected="true"] .btn,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .btn.active,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .btn.btn-primary {
        position: relative;
        box-shadow: 0 0 0 2px var(--vt-card-bg, #ffffff), 0 0 0 4px #0284c7, 0 2px 8px rgba(2, 132, 199, 0.45) !important;
        font-weight: 800 !important;
        opacity: 1 !important;
        z-index: 2;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-picked .btn,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col[aria-selected="true"] .btn,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .btn.active,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .btn.btn-primary {
        box-shadow: 0 0 0 2px var(--vt-card-bg, #1a2332), 0 0 0 4px #38bdf8, 0 0 12px rgba(56, 189, 248, 0.6) !important;
        font-weight: 800 !important;
        opacity: 1 !important;
        z-index: 2;
    }

    /* When Today is Selected */
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today.cal-picked .btn,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today[aria-selected="true"] .btn,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.btn-primary,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.active {
        box-shadow: 0 0 0 2px var(--vt-card-bg, #ffffff), 0 0 0 4px #0284c7, 0 0 10px rgba(2, 132, 199, 0.5) !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today.cal-picked .btn,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today[aria-selected="true"] .btn,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.btn-primary,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.active {
        box-shadow: 0 0 0 2px var(--vt-card-bg, #1a2332), 0 0 0 4px #38bdf8, 0 0 12px rgba(56, 189, 248, 0.6) !important;
    }
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today.cal-picked .btn::after,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today[aria-selected="true"] .btn::after,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.btn-primary::after,
    .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.active::after {
        background-color: #0284c7 !important;
        box-shadow: 0 0 3px rgba(2, 132, 199, 0.5) !important;
    }
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today.cal-picked .btn::after,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today[aria-selected="true"] .btn::after,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.btn-primary::after,
    [data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn.active::after {
        background-color: #38bdf8 !important;
        box-shadow: 0 0 4px #38bdf8 !important;
    }
    .work-items-grid ::v-deep .grid-row > [class*="grid-col-"],
    .work-items-grid ::v-deep .grid-row > .time-col-custom,
    .activity-items-grid ::v-deep .grid-row > [class*="grid-col-"],
    .absence-items-grid ::v-deep .grid-row > [class*="grid-col-"] {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }
    .work-items-grid .grid-col-type {
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: center;
    }
    .work-items-grid .workitem-type-image {
        padding-top: 0 !important;
        width: 18px;
        height: 18px;
        line-height: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .work-items-grid .workitem-type-image ::v-deep img,
    .work-items-grid .workitem-type-image ::v-deep svg {
        width: 18px;
        height: 18px;
        display: block;
    }
    .work-items-grid .grid-col-title {
        line-height: 1.3;
        min-width: 0;
        position: relative;
        justify-content: flex-start;
        text-align: left;
    }
    .title-text {
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        line-height: 1.3;
    }
    .title-text-measure {
        position: absolute;
        visibility: hidden;
        pointer-events: none;
        top: 0;
        left: 0;
        right: 0;
        z-index: -1;
        display: block !important;
        overflow: visible !important;
        -webkit-line-clamp: unset !important;
        line-clamp: unset !important;
    }
    .absence-items-grid .grid-col-worktype {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }
    .time-row-shell.time-row-clamped .title-text {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .time-row-shell:not(.time-row-clamped) .title-text {
        display: block;
        overflow: visible;
    }
    .work-expander {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 4px;
        margin-top: 1px;
        color: transparent;
        cursor: pointer;
        border-radius: 0 0 4px 4px;
        background-color: #e9ecef;
        transition: height 0.15s ease, color 0.15s ease, background-color 0.15s ease;
    }
    .work-expander:hover {
        height: 12px;
        color: #6c757d;
        background-color: #dde1e5;
    }
    .work-expander > i {
        font-size: 0.65rem;
        line-height: 1;
    }
    .work-items-grid .grid-col-buttons-logs,
    .work-items-grid .grid-col-buttons-azure,
    .work-items-grid .grid-col-buttons-actions,
    .activity-items-grid .grid-col-buttons {
        overflow: visible;
    }

    .work-items-grid {
        font-size: 0.875rem;
        overflow: visible;
        margin-bottom: 0.75rem;
    }
    .activity-items-grid {
        font-size: 0.875rem;
        width: 100%;
        margin-top: 0.75rem;
    }
    .absence-items-grid {
        font-size: 0.875rem;
        width: max-content;
        max-width: 100%;
        margin-left: auto;
        margin-right: auto;
        margin-top: 1.5rem;
    }
    .time-row-shell {
        display: block;
        width: 100%;
        box-sizing: border-box;
        padding: 2px 4px;
        margin: 0.25rem 0;
    }

    .activity-row-shake,
    .time-row-shake {
        position: relative;
        z-index: 2;
        animation: activity-shake-anim 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both, activity-glow-anim 1.4s ease-out both;
        border-radius: 6px;
        will-change: transform;
        --act-glow-color-start: rgba(245, 158, 11, 0.28);
        --act-glow-shadow-start: 0 0 0 3px rgba(245, 158, 11, 0.65), 0 4px 16px rgba(245, 158, 11, 0.35);
        --act-glow-color-mid: rgba(245, 158, 11, 0.14);
        --act-glow-shadow-mid: 0 0 0 2px rgba(245, 158, 11, 0.4), 0 2px 8px rgba(245, 158, 11, 0.15);
    }
    [data-theme="dark"] .activity-row-shake,
    [data-theme="dark"] .time-row-shake {
        --act-glow-color-start: rgba(251, 191, 36, 0.32);
        --act-glow-shadow-start: 0 0 0 3px rgba(251, 191, 36, 0.75), 0 4px 18px rgba(251, 191, 36, 0.45);
        --act-glow-color-mid: rgba(251, 191, 36, 0.18);
        --act-glow-shadow-mid: 0 0 0 2px rgba(251, 191, 36, 0.5), 0 2px 10px rgba(251, 191, 36, 0.25);
    }

    @keyframes activity-shake-anim {
        10%, 90% {
            transform: translate3d(-3px, 0, 0);
        }
        20%, 80% {
            transform: translate3d(5px, 0, 0);
        }
        30%, 50%, 70% {
            transform: translate3d(-7px, 0, 0);
        }
        40%, 60% {
            transform: translate3d(7px, 0, 0);
        }
    }

    @keyframes activity-glow-anim {
        0% {
            background-color: var(--act-glow-color-start);
            box-shadow: var(--act-glow-shadow-start);
        }
        60% {
            background-color: var(--act-glow-color-mid);
            box-shadow: var(--act-glow-shadow-mid);
        }
        100% {
            background-color: transparent;
            box-shadow: none;
        }
    }

    .time-section-title {
        font-size: 1rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.75rem;
        padding-bottom: 0.4rem;
        border-bottom: 2px solid #dee2e6;
    }
    .work-items-grid .work-progress {
        min-width: 0;
    }
    .work-items-grid .work-progress-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 0.15rem;
    }
    .work-items-grid .work-progress-hours {
        font-size: 0.8rem;
        font-weight: 600;
        white-space: nowrap;
    }
    .work-items-grid .work-progress-meta {
        margin-top: 0.15rem;
        font-size: 0.7rem;
        color: #6c757d;
        white-space: nowrap;
    }
    .work-items-grid .title-toggle,
    .activity-items-grid .title-toggle {
        padding: 0 0 0 4px;
        font-size: 0.875rem;
        line-height: 1;
        vertical-align: baseline;
        flex: 0 0 auto;
        white-space: nowrap;
    }
    .work-items-grid .grid-col-state,
    .work-items-grid .grid-col-progress {
        position: relative;
        z-index: 2;
        overflow: hidden;
    }
    .work-items-grid ::v-deep .form-control,
    .work-items-grid ::v-deep .custom-select,
    .work-items-grid ::v-deep .input-group-text,
    .activity-items-grid ::v-deep .form-control,
    .activity-items-grid ::v-deep .custom-select,
    .activity-items-grid ::v-deep .input-group-text,
    .absence-items-grid ::v-deep .form-control,
    .absence-items-grid ::v-deep .custom-select,
    .absence-items-grid ::v-deep .input-group-text {
        font-size: 0.875rem;
        padding: 0.2rem 0.4rem;
        height: calc(1.5em + 0.5rem + 2px);
        min-height: 1.9rem;
    }
    .activity-items-grid .grid-col-comment ::v-deep textarea.form-control {
        width: 100%;
        min-width: 0;
        max-width: none;
        min-height: 1.9rem;
        overflow: auto;
        resize: vertical;
        line-height: 1.35;
        white-space: pre-wrap;
        word-break: break-word;
    }
    .work-items-grid ::v-deep .btn,
    .activity-items-grid ::v-deep .btn,
    .absence-items-grid ::v-deep .btn {
        font-size: 0.875rem;
        padding: 0.2rem 0.4rem;
    }


    .absence-items-grid .grid-col-hours {
        max-width: none;
        flex: none;
    }
    .absence-items-grid .grid-col-buttons {
        max-width: 78px !important;
    }

    .work-items-grid .grid-col-hours ::v-deep .input-group,
    .work-items-grid .grid-col-changecapacity ::v-deep .input-group,
    .work-items-grid .time-col-custom ::v-deep .input-group,
    .activity-items-grid .grid-col-hours ::v-deep .input-group,
    .absence-items-grid .grid-col-hours ::v-deep .input-group {
        flex-wrap: nowrap;
        width: auto;
    }
    .work-items-grid .grid-col-hours .completed-work-row,
    .activity-items-grid .grid-col-hours .completed-work-row {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 0.25rem;
    }
    .work-items-grid .grid-col-hours .completed-work-row > *,
    .activity-items-grid .grid-col-hours .completed-work-row > * {
        flex: 0 0 auto;
        min-width: 0;
    }
    .work-items-grid .grid-col-hours .completed-work-row ::v-deep .input-group,
    .activity-items-grid .grid-col-hours .completed-work-row ::v-deep .input-group {
        flex-wrap: nowrap;
        width: auto;
        flex: 1 1 auto;
        min-width: 0;
        margin-top: 0;
    }
    .work-items-grid .grid-col-hours ::v-deep .form-control,
    .work-items-grid .grid-col-changecapacity ::v-deep .form-control,
    .work-items-grid .time-col-custom ::v-deep input[type="number"].form-control,
    .activity-items-grid .grid-col-hours ::v-deep .form-control,
    .absence-items-grid .grid-col-hours ::v-deep .form-control {
        width: 3.1rem !important;
        max-width: 3.1rem !important;
        min-width: 3.1rem !important;
        flex: 0 0 3.1rem !important;
        padding-left: 0.2rem !important;
        padding-right: 0.1rem !important;
        text-align: center;
    }
    .work-items-grid .grid-col-hours ::v-deep input[type="number"]::-webkit-inner-spin-button,
    .work-items-grid .grid-col-changecapacity ::v-deep input[type="number"]::-webkit-inner-spin-button,
    .work-items-grid .time-col-custom ::v-deep input[type="number"]::-webkit-inner-spin-button,
    .activity-items-grid .grid-col-hours ::v-deep input[type="number"]::-webkit-inner-spin-button,
    .absence-items-grid .grid-col-hours ::v-deep input[type="number"]::-webkit-inner-spin-button {
        opacity: 1;
        cursor: pointer;
    }
    [data-theme="dark"] .work-items-grid .grid-col-hours ::v-deep input[type="number"],
    [data-theme="dark"] .work-items-grid .grid-col-changecapacity ::v-deep input[type="number"],
    [data-theme="dark"] .work-items-grid .time-col-custom ::v-deep input[type="number"],
    [data-theme="dark"] .activity-items-grid .grid-col-hours ::v-deep input[type="number"],
    [data-theme="dark"] .absence-items-grid .grid-col-hours ::v-deep input[type="number"] {
        color-scheme: dark;
    }
    .work-items-grid .grid-col-hours ::v-deep .input-group-text,
    .work-items-grid .grid-col-changecapacity ::v-deep .input-group-text,
    .work-items-grid .time-col-custom ::v-deep .input-group-text,
    .activity-items-grid .grid-col-hours ::v-deep .input-group-text,
    .absence-items-grid .grid-col-hours ::v-deep .input-group-text {
        padding-left: 0.4rem;
        padding-right: 0.4rem;
        font-size: 0.875rem;
        white-space: nowrap;
    }
    .work-items-grid .grid-col-worktype ::v-deep .custom-select {
        width: 100%;
        min-width: 0;
        max-width: 7.5rem;
    }
    .work-items-grid ::v-deep .custom-select,
    .activity-items-grid ::v-deep .custom-select,
    .absence-items-grid ::v-deep .custom-select {
        width: 100%;
        min-width: 0;
        padding-right: 1.6rem !important;
    }
    .activity-items-grid .grid-col-worktype ::v-deep .custom-select {
        width: 100%;
        min-width: 0;
        max-width: 14rem;
    }
    .activity-items-grid .grid-col-comment {
        width: 100%;
        min-width: 0;
        max-width: none;
    }
    .activity-items-grid .grid-col-comment ::v-deep .form-control,
    .activity-items-grid .grid-col-comment ::v-deep textarea {
        width: 100%;
        min-width: 0;
        max-width: none;
    }

    .calendar-summary-row {
        align-items: stretch;
    }
    .calendar-side-col {
        display: flex;
        padding-top: 2.35rem;
        padding-bottom: 2.35rem;
    }
    .calendar-side-overlay {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        min-height: 20.5rem;
    }
    .calendar-side-overlay ::v-deep .b-overlay-wrap {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        min-height: 20.5rem;
    }
    .calendar-card-overlay {
        width: 100%;
        display: block;
    }
    .time-items-overlay {
        min-height: 220px;
        width: 100%;
        display: block;
    }
    .time-progress-card {
        background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
        border: 1px solid #e9ecef;
        border-radius: 0.75rem;
        padding: 0.1rem 1.1rem;
        box-shadow: 0 8px 20px rgba(33, 37, 41, 0.06);
        width: 100%;
        min-height: 20.5rem;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
    .month-worktype-chart {
        min-height: 20.5rem;
    }
    .month-worktype-chart-canvas {
        position: relative;
        height: 188px;
        max-height: 188px;
    }
    .month-worktype-chart-center {
        position: absolute;
        top: 38%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        pointer-events: none;
    }
    .month-worktype-chart-total {
        font-size: 1.35rem;
        font-weight: 700;
        line-height: 1.1;
        color: #212529;
    }
    .month-worktype-chart-total-label {
        font-size: 0.72rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #868e96;
    }
    .month-worktype-chart-empty {
        min-height: 10rem;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 0.9rem;
    }
    .time-progress-title {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #6c757d;
        margin-bottom: 0.85rem;
        text-align: center;
    }
    .time-progress-item + .time-progress-item {
        margin-top: 0.95rem;
    }
    .time-progress-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.75rem;
        margin-bottom: 0.3rem;
    }
    .time-progress-label {
        font-size: 0.95rem;
        font-weight: 600;
        color: #343a40;
    }
    .time-progress-hours {
        font-size: 0.82rem;
        color: #495057;
        white-space: nowrap;
    }
    .time-progress-meta {
        margin-top: 0.25rem;
        font-size: 0.75rem;
        color: #6c757d;
    }

    .grid-col-workitemid {
        position: relative;
        overflow: visible;
    }
    .calendar-day-legend {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 0.75rem 1.25rem;
        margin-top: 0.75rem;
        font-size: 0.875rem;
    }
    .calendar-legend-weekend {
        background-color: #64748b !important;
    }
    .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
    }
    .legend-swatch {
        width: 1rem;
        height: 1rem;
        border-radius: 3px;
        display: inline-block;
        padding: 0;
        border: 1px solid rgba(0, 0, 0, 0.12);
    }
    .legend-swatch.table-success {
        background-color: #16a34a !important;
    }
    .legend-swatch.table-warning {
        background-color: #d97706 !important;
    }
    .legend-swatch.table-danger {
        background-color: #dc2626 !important;
    }
    .legend-swatch.table-info {
        background-color: #0284c7 !important;
    }
    .legend-swatch.legend-swatch-today {
        background-color: transparent !important;
        border: 2px solid #0284c7 !important;
        border-radius: 50% !important;
        position: relative;
    }
    .legend-swatch.legend-swatch-today::after {
        content: '';
        position: absolute;
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: #0284c7;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
    [data-theme="dark"] .legend-swatch.legend-swatch-today {
        border-color: #38bdf8 !important;
        box-shadow: 0 0 6px rgba(56, 189, 248, 0.4);
    }
    [data-theme="dark"] .legend-swatch.legend-swatch-today::after {
        background-color: #38bdf8;
    }

    .time-action-button {
        font-size: 1.5rem;
        padding: 14px;
        margin: 10px;
    }

    .work-items-grid .grid-col-state .state-btn {
        opacity: 1 !important;
    }

    .absence-items-grid .grid-col-hours {
        display: flex;
        justify-content: center;
    }
    .absence-items-grid .grid-col-hours ::v-deep .form-control:disabled {
        opacity: 1;
        color: inherit;
        background-color: transparent;
        cursor: default;
    }

    .action-buttons {
        white-space: nowrap;
    }
    .action-buttons ::v-deep .btn-group {
        flex-wrap: nowrap;
    }
    .action-buttons button {
        margin: 0;
    }

    /* Dark Mode Polish */
    [data-theme="dark"] .time-section-title {
        color: #f1f5f9 !important;
        border-bottom-color: #2d3748 !important;
    }
    [data-theme="dark"] .time-progress-label {
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .time-progress-hours {
        color: #cbd5e1 !important;
    }
    [data-theme="dark"] .time-progress-meta {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-secondary {
        color: #e2e8f0 !important;
        border-color: #4b5d73 !important;
        background-color: #1a2332 !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-secondary:hover:not(:disabled) {
        color: #ffffff !important;
        border-color: #007bff !important;
        background-color: #007bff !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-secondary:disabled {
        color: #64748b !important;
        border-color: #2d3748 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-primary {
        color: #38bdf8 !important;
        border-color: #0284c7 !important;
        background-color: #1a2332 !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-primary:hover:not(:disabled) {
        color: #ffffff !important;
        border-color: #007bff !important;
        background-color: #007bff !important;
    }
    [data-theme="dark"] .calendar-nav-bar .btn-outline-primary:disabled {
        color: #64748b !important;
        border-color: #2d3748 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .work-items-grid .project-work-group .card.bg-light {
        background-color: #17202c !important;
        border-color: #2d3748 !important;
    }

    .shortcut-badge {
        font-size: 0.65rem;
        padding: 1px 5px;
        background-color: rgba(0, 0, 0, 0.08);
        color: inherit;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        vertical-align: middle;
        opacity: 0.85;
    }

    [data-theme="dark"] .shortcut-badge {
        background-color: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.2);
        color: #e2e8f0;
    }

    /* Selected Picked Date Banner */
    .picked-date-banner {
        background-color: var(--vt-card-bg, #ffffff);
        border: 1px solid var(--vt-card-border, #e2e8f0);
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: background-color 0.2s ease, border-color 0.2s ease;
        overflow: hidden;
        max-width: 100%;
    }
    .picked-date-header-row {
        min-width: 0;
    }
    .picked-date-main-info {
        min-width: 0;
    }
    [data-theme="dark"] .picked-date-banner {
        background-color: #1a2332;
        border-color: #2d3748;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    }
    .picked-date-calendar-icon {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        background: rgba(2, 132, 199, 0.1);
        color: #0284c7;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
        flex-shrink: 0;
    }
    [data-theme="dark"] .picked-date-calendar-icon {
        background: rgba(56, 189, 248, 0.15);
        color: #38bdf8;
    }
    .picked-date-text {
        font-size: 1.15rem;
        font-weight: 700;
        color: var(--vt-text, #1e293b);
        line-height: 1.3;
        min-width: 0;
    }
    .picked-date-pill {
        font-size: 0.72rem;
        font-weight: 600;
        padding: 0.2rem 0.5rem;
        border-radius: 12px;
    }
    .picked-date-subtext {
        font-size: 0.8rem;
        margin-top: 2px;
    }
    .picked-date-badge-pill {
        display: inline-flex;
        align-items: center;
        padding: 0.35rem 0.85rem;
        border-radius: 20px;
        font-size: 0.95rem;
        font-weight: 600;
        line-height: 1.3;
        border: 1.5px solid transparent;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        transition: all 0.2s ease;
    }
    .picked-date-badge-pill .status-icon {
        font-size: 1.15rem;
    }
    .picked-date-badge-pill .status-label {
        font-size: 0.95rem;
        font-weight: 600;
        letter-spacing: 0.01em;
    }
    .picked-date-badge-pill .status-value {
        font-size: 1.18rem;
        font-weight: 800;
        letter-spacing: 0.02em;
    }
    .picked-date-badge-pill.status-overspend {
        background-color: rgba(220, 38, 38, 0.15);
        color: #b91c1c;
        border-color: rgba(220, 38, 38, 0.45);
    }
    [data-theme="dark"] .picked-date-badge-pill.status-overspend {
        background-color: rgba(239, 68, 68, 0.25);
        color: #fca5a5;
        border-color: rgba(239, 68, 68, 0.5);
    }
    .picked-date-badge-pill.status-met {
        background-color: rgba(22, 163, 74, 0.15);
        color: #15803d;
        border-color: rgba(22, 163, 74, 0.45);
    }
    [data-theme="dark"] .picked-date-badge-pill.status-met {
        background-color: rgba(34, 197, 94, 0.25);
        color: #86efac;
        border-color: rgba(34, 197, 94, 0.5);
    }
    .picked-date-badge-pill.status-behind {
        background-color: rgba(217, 119, 6, 0.15);
        color: #b45309;
        border-color: rgba(217, 119, 6, 0.45);
    }
    [data-theme="dark"] .picked-date-badge-pill.status-behind {
        background-color: rgba(245, 158, 11, 0.25);
        color: #fde68a;
        border-color: rgba(245, 158, 11, 0.5);
    }
    .picked-date-badge-pill.status-info,
    .picked-date-badge-pill.status-capacity {
        background-color: rgba(2, 132, 199, 0.15);
        color: #0369a1;
        border-color: rgba(2, 132, 199, 0.45);
    }
    [data-theme="dark"] .picked-date-badge-pill.status-info,
    [data-theme="dark"] .picked-date-badge-pill.status-capacity {
        background-color: rgba(56, 189, 248, 0.25);
        color: #7dd3fc;
        border-color: rgba(56, 189, 248, 0.5);
    }
    .picked-date-badge-pill.status-weekend {
        background-color: rgba(100, 116, 139, 0.15);
        color: #475569;
        border-color: rgba(100, 116, 139, 0.35);
    }
    [data-theme="dark"] .picked-date-badge-pill.status-weekend {
        background-color: rgba(148, 163, 184, 0.2);
        color: #e2e8f0;
        border-color: rgba(148, 163, 184, 0.4);
    }

    @media (max-width: 991.98px) {
        .picked-date-banner {
            margin-top: 0 !important;
            margin-bottom: 0.65rem !important;
            padding: 0.45rem 0.65rem !important;
        }

        .picked-date-calendar-icon {
            width: 30px !important;
            height: 30px !important;
            font-size: 1rem !important;
            border-radius: 6px !important;
        }

        .picked-date-text {
            font-size: 0.95rem !important;
            white-space: nowrap !important;
        }

        .picked-date-pill {
            font-size: 0.68rem !important;
            padding: 0.15rem 0.4rem !important;
        }

        .picked-date-badge-pill {
            font-size: 0.78rem !important;
            padding: 0.2rem 0.5rem !important;
        }

        .picked-date-badge-pill .status-icon {
            font-size: 0.95rem !important;
        }

        .picked-date-badge-pill .status-label {
            font-size: 0.78rem !important;
        }

        .picked-date-badge-pill .status-value {
            font-size: 0.88rem !important;
        }
    }

    @media (max-width: 767.98px) {
        .picked-date-banner {
            padding: 0.35rem 0.5rem !important;
            gap: 0.25rem !important;
            overflow: hidden !important;
            max-width: 100% !important;
        }

        .picked-date-header-row {
            min-width: 0 !important;
        }

        .picked-date-calendar-icon {
            width: 24px !important;
            height: 24px !important;
            font-size: 0.82rem !important;
            border-radius: 5px !important;
            margin-right: 0.35rem !important;
        }

        .picked-date-text {
            font-size: 0.84rem !important;
            letter-spacing: 0.1px !important;
            white-space: nowrap !important;
        }

        .picked-date-pill {
            font-size: 0.65rem !important;
            padding: 0.12rem 0.35rem !important;
            max-width: 110px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
        }

        .picked-date-badge-pill {
            font-size: 0.72rem !important;
            padding: 0.15rem 0.45rem !important;
            border-radius: 14px !important;
            white-space: nowrap !important;
        }

        .picked-date-badge-pill .status-icon {
            font-size: 0.82rem !important;
            margin-right: 0.25rem !important;
        }

        .picked-date-badge-pill .status-label {
            font-size: 0.72rem !important;
            margin-right: 0.2rem !important;
        }

        .picked-date-badge-pill .status-value {
            font-size: 0.82rem !important;
        }
    }

    @media (max-width: 359.98px) {
        .picked-date-banner {
            padding: 0.28rem 0.38rem !important;
        }

        .picked-date-calendar-icon {
            width: 22px !important;
            height: 22px !important;
            font-size: 0.75rem !important;
            border-radius: 4px !important;
            margin-right: 0.25rem !important;
        }

        .picked-date-text {
            font-size: 0.8rem !important;
        }

        .picked-date-pill {
            font-size: 0.62rem !important;
            padding: 0.1rem 0.28rem !important;
            max-width: 90px !important;
        }

        .picked-date-badge-pill {
            font-size: 0.68rem !important;
            padding: 0.12rem 0.35rem !important;
        }

        .picked-date-badge-pill .status-icon {
            font-size: 0.76rem !important;
            margin-right: 0.2rem !important;
        }

        .picked-date-badge-pill .status-label {
            font-size: 0.68rem !important;
            margin-right: 0.15rem !important;
        }

        .picked-date-badge-pill .status-value {
            font-size: 0.76rem !important;
        }
    }

    /* Value color select styling */
    .value-color-select {
        background-color: #f1f5f9 !important;
        border-color: #cbd5e1 !important;
        color: #0f172a !important;
    }

    [data-theme="dark"] .value-color-select {
        background-color: #1e293b !important;
        border-color: #475569 !important;
        color: #f8fafc !important;
    }

    .value-color-select[style*="--val-color"] {
        transition: background-color 0.2s ease, border-color 0.2s ease;
        font-weight: 500;
    }

    [data-theme="dark"] .value-color-select[style*="--val-color"] {
        background-color: color-mix(in srgb, var(--val-color) 25%, rgba(15, 23, 42, 0.9)) !important;
        border-color: color-mix(in srgb, var(--val-color) 60%, transparent) !important;
        color: #ffffff !important;
    }

    [data-theme="light"] .value-color-select[style*="--val-color"],
    :root:not([data-theme="dark"]) .value-color-select[style*="--val-color"] {
        background-color: color-mix(in srgb, var(--val-color) 18%, #f1f5f9) !important;
        border-color: color-mix(in srgb, var(--val-color) 55%, #cbd5e1) !important;
        color: #0f172a !important;
    }
</style>
<style>
    .action-hover-pop {
        pointer-events: none;
        z-index: 1030;
    }
</style>
<style scoped>
    .work-items-grid ::v-deep .time-col-custom .custom-select,
    .work-items-grid ::v-deep .time-col-custom .form-control {
        font-size: 0.8rem;
        padding: 0.2rem 0.35rem;
        height: calc(1.5em + 0.5rem + 2px);
        min-height: 1.9rem;
    }
    .work-items-grid ::v-deep .time-col-custom .custom-select {
        padding-right: 1.6rem !important;
    }
    .custom-field-na {
        color: #94a3b8;
        font-weight: 600;
        font-size: 0.9rem;
        user-select: none;
    }
    [data-theme="dark"] .custom-field-na {
        color: #64748b;
    }
    /* Mobile Stepper & Toggle Button Styles */
    .mobile-date-toolbar {
        width: 100%;
        padding-top: 0.35rem;
        border-top: none !important;
    }

    .mobile-mini-metrics {
        font-size: 0.78rem;
        color: var(--vt-text, inherit);
        white-space: nowrap;
    }

    .mobile-day-stepper {
        gap: 0.25rem;
    }

    .mobile-stepper-btn {
        min-height: 34px !important;
        height: 34px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0.25rem 0.45rem !important;
        font-size: 0.85rem !important;
        border-radius: 0.45rem !important;
        line-height: 1 !important;
    }

    .mobile-step-arrow {
        width: 34px !important;
        min-width: 34px !important;
        max-width: 34px !important;
        padding: 0 !important;
        font-size: 1rem !important;
    }

    .mobile-step-today {
        padding: 0.25rem 0.5rem !important;
        font-size: 0.85rem !important;
        font-weight: 700 !important;
        letter-spacing: 0.2px;
    }

    .mobile-insights-toggle-btn {
        min-height: 34px !important;
        height: 34px !important;
        padding: 0.25rem 0.55rem !important;
        font-size: 0.82rem !important;
        font-weight: 600 !important;
        border-radius: 0.45rem !important;
        line-height: 1 !important;
    }

    .mobile-insights-toggle-btn i {
        font-size: 0.95rem !important;
    }

    .time-action-col {
        gap: 0.5rem;
    }

    .time-action-button .action-btn-icon {
        margin-right: 0.35rem;
    }

    .time-action-button .action-btn-icon-wrap {
        position: relative !important;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        margin-right: 0.75rem !important;
    }

    .time-action-button .reconcile-badge-count,
    .btn.time-action-button .badge.reconcile-badge-count,
    .reconcile-badge-count {
        position: absolute !important;
        top: -6px !important;
        right: -8px !important;
        font-size: 0.65rem !important;
        font-weight: 700 !important;
        line-height: 1 !important;
        padding: 0.1rem 0.3rem !important;
        min-width: 16px !important;
        height: 16px !important;
        border-radius: 999px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        pointer-events: none !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35) !important;
        z-index: 2 !important;
    }

    @media (max-width: 991.98px) {
        .calendar-center-col {
            order: -1;
            display: flex;
            flex-direction: column;
        }

        .picked-date-banner {
            order: -1;
            margin-top: 0 !important;
            margin-bottom: 0.65rem;
            padding: 0.45rem 0.65rem;
        }

        .picked-date-calendar-icon {
            width: 30px;
            height: 30px;
            font-size: 1rem;
            border-radius: 6px;
        }

        .picked-date-text {
            font-size: 0.95rem;
            white-space: nowrap;
        }

        .picked-date-pill {
            font-size: 0.68rem;
            padding: 0.15rem 0.4rem;
        }

        .picked-date-badge-pill {
            font-size: 0.78rem;
            padding: 0.2rem 0.5rem;
        }

        .picked-date-badge-pill .status-icon {
            font-size: 0.95rem;
        }

        .picked-date-badge-pill .status-label {
            font-size: 0.78rem;
        }

        .picked-date-badge-pill .status-value {
            font-size: 0.88rem;
        }

        /* Compact, Narrower & Bigger Action Button Bar */
        .time-action-row {
            margin-top: 0.35rem !important;
            margin-bottom: 0.35rem !important;
        }

        .time-action-col {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            justify-content: center !important;
            align-items: center !important;
            gap: 0.45rem !important;
            padding-left: 0.15rem !important;
            padding-right: 0.15rem !important;
            width: 100% !important;
        }

        .time-action-button.time-action-export {
            display: none !important;
        }

        .time-action-button {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 68px !important;
            width: 68px !important;
            min-width: 64px !important;
            max-width: 72px !important;
            min-height: 54px !important;
            height: 54px !important;
            padding: 0.35rem 0.2rem 0.25rem !important;
            margin: 0 !important;
            border-radius: 0.5rem !important;
            text-align: center !important;
            line-height: 1.15 !important;
        }

        .time-action-button .action-btn-icon {
            font-size: 1.5rem !important;
            line-height: 1 !important;
            margin: 0 0 0.18rem 0 !important;
            display: inline-block !important;
        }

        .time-action-button .action-btn-icon-wrap {
            display: inline-flex !important;
            justify-content: center !important;
            align-items: center !important;
            margin: 0 0 0.18rem 0 !important;
        }

        .time-action-button .action-btn-icon-wrap .action-btn-icon {
            margin: 0 !important;
        }

        .time-action-button .action-btn-label {
            font-size: 0.74rem !important;
            font-weight: 600 !important;
            line-height: 1.15 !important;
            letter-spacing: 0.01em !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 100% !important;
            display: block !important;
        }

        .time-action-button .shortcut-badge {
            display: none !important;
        }

        .grid-empty-actions .btn {
            font-size: 0.78rem !important;
            padding: 0.22rem 0.5rem !important;
        }

        .calendar-collapsible-body {
            order: 1;
            margin-bottom: 0.5rem;
        }

        .calendar-summary-row.mobile-insights-collapsed .calendar-collapsible-body {
            display: none !important;
        }

        .calendar-side-col {
            display: none !important;
        }
    }

    @media (max-width: 767.98px) {
        .picked-date-banner {
            padding: 0.35rem 0.5rem;
            gap: 0.25rem;
        }

        .picked-date-calendar-icon {
            width: 26px;
            height: 26px;
            font-size: 0.85rem;
            border-radius: 5px;
        }

        .picked-date-text {
            font-size: 0.86rem;
            letter-spacing: 0.1px;
            white-space: nowrap;
        }

        .picked-date-pill {
            font-size: 0.65rem;
            padding: 0.12rem 0.35rem;
        }

        .picked-date-badge-pill {
            font-size: 0.72rem;
            padding: 0.15rem 0.45rem;
        }

        .picked-date-badge-pill .status-icon {
            font-size: 0.85rem;
        }

        .picked-date-badge-pill .status-label {
            font-size: 0.72rem;
        }

        .picked-date-badge-pill .status-value {
            font-size: 0.82rem;
        }

        /* Compact action button bar on phones */
        .time-action-row {
            margin-top: 0.25rem !important;
            margin-bottom: 0.25rem !important;
        }

        .time-action-col {
            gap: 0.35rem !important;
            padding-left: 0.1rem !important;
            padding-right: 0.1rem !important;
            justify-content: center !important;
        }

        .time-action-button {
            flex: 0 0 66px !important;
            width: 66px !important;
            min-width: 62px !important;
            max-width: 68px !important;
            min-height: 54px !important;
            height: 54px !important;
            padding: 0.35rem 0.15rem 0.25rem !important;
            border-radius: 0.45rem !important;
        }

        .time-action-button .action-btn-icon {
            font-size: 1.45rem !important;
            margin-bottom: 0.15rem !important;
        }

        .time-action-button .action-btn-icon-wrap {
            margin-right: 0 !important;
            margin-bottom: 0.15rem !important;
        }

        .time-action-button .action-btn-label {
            font-size: 0.72rem !important;
            letter-spacing: -0.2px !important;
        }

        .time-action-button .reconcile-badge-count,
        .btn.time-action-button .badge.reconcile-badge-count,
        .reconcile-badge-count {
            top: -6px !important;
            right: -8px !important;
            font-size: 0.6rem !important;
            padding: 0.08rem 0.26rem !important;
            min-width: 15px !important;
            height: 15px !important;
        }

        .shortcut-badge {
            display: none !important;
        }
    }

    /* Mobile & Tablet Structured Card Layout for Work Items, Activities & Absences (<=991.98px) */
    @media (max-width: 991.98px) {
        .mobile-col-caption {
            display: block !important;
            font-size: 0.65rem !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            color: #64748b !important;
            margin-bottom: 0.25rem !important;
            line-height: 1 !important;
            white-space: nowrap !important;
            user-select: none;
        }

        [data-theme="dark"] .mobile-col-caption {
            color: #94a3b8 !important;
        }

        .work-items-grid .time-row-shell,
        .activity-items-grid .time-row-shell,
        .absence-items-grid .grid-row {
            background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.45));
            border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.22));
            border-radius: 0.65rem;
            padding: 0.75rem 0.85rem !important;
            margin-bottom: 0.75rem !important;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
        }

        [data-theme="dark"] .work-items-grid .time-row-shell,
        [data-theme="dark"] .activity-items-grid .time-row-shell,
        [data-theme="dark"] .absence-items-grid .grid-row {
            background-color: rgba(30, 41, 59, 0.65) !important;
            border-color: rgba(148, 163, 184, 0.24) !important;
        }

        [data-theme="light"] .work-items-grid .time-row-shell,
        [data-theme="light"] .activity-items-grid .time-row-shell,
        [data-theme="light"] .absence-items-grid .grid-row {
            background-color: #ffffff !important;
            border-color: rgba(203, 213, 225, 0.85) !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .work-items-grid .time-row-shell .grid-row,
        .activity-items-grid .time-row-shell .grid-row,
        .absence-items-grid .grid-row {
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: flex-start !important;
            padding: 0 !important;
            gap: 0.5rem 0.65rem !important;
            height: auto !important;
        }

        /* Top row of card: ID + State Badge (order 1) */
        .work-items-grid .time-row-shell .grid-row > .grid-col-workitemid {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
        }

        .work-items-grid .time-row-shell .grid-row > .grid-col-state {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            margin-left: 0.25rem;
        }

        /* Second row of card: Full-width Title */
        .work-items-grid .time-row-shell .grid-row > .grid-col-title {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-top: 0.2rem;
            margin-bottom: 0.35rem;
            text-align: left !important;
        }

        .work-items-grid .grid-col-title .title-text {
            font-size: 0.95rem;
            font-weight: 600;
            color: #0f172a;
            line-height: 1.35;
        }

        [data-theme="dark"] .work-items-grid .grid-col-title .title-text {
            color: #f8fafc;
        }

        /* Third row of card: Progress bar */
        .work-items-grid .time-row-shell .grid-row > .grid-col-progress {
            order: 3 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-bottom: 0.35rem;
        }

        /* Fourth row of card: Work Type */
        .work-items-grid .time-row-shell .grid-row > .grid-col-worktype {
            order: 4 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
            margin-bottom: 0.35rem;
        }

        .work-items-grid .grid-col-worktype ::v-deep .custom-select {
            height: 31px !important;
            font-size: 0.85rem !important;
            min-width: 140px;
            max-width: 220px;
            width: 100%;
        }

        /* Stepper Buttons for Mobile Numeric Inputs */
        .stepper-btn {
            width: 25px !important;
            min-width: 25px !important;
            max-width: 25px !important;
            height: 31px !important;
            padding: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 0.95rem !important;
            line-height: 1 !important;
            color: var(--vt-text, inherit) !important;
            border-color: rgba(148, 163, 184, 0.3) !important;
            background-color: transparent !important;
            user-select: none;
        }

        .stepper-btn:hover:not(:disabled) {
            background-color: rgba(148, 163, 184, 0.15) !important;
        }

        .stepper-btn:active:not(:disabled) {
            background-color: rgba(148, 163, 184, 0.25) !important;
        }

        .stepper-btn:disabled {
            opacity: 0.35 !important;
            cursor: not-allowed;
        }

        [data-theme="dark"] .stepper-btn {
            color: #e2e8f0 !important;
            border-color: rgba(148, 163, 184, 0.25) !important;
        }

        [data-theme="light"] .stepper-btn {
            color: #334155 !important;
            border-color: rgba(203, 213, 225, 0.9) !important;
        }

        @media (min-width: 992px) {
            .stepper-input-group > .form-control {
                border-top-left-radius: 0.2rem !important;
                border-bottom-left-radius: 0.2rem !important;
            }
        }

        /* Fifth row of card: Logged hours (full width on mobile) */
        .work-items-grid .time-row-shell .grid-row > .grid-col-hours,
        .activity-items-grid .time-row-shell .grid-row > .grid-col-hours {
            order: 5 !important;
            flex: 1 1 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            margin-bottom: 0.35rem !important;
        }

        .work-items-grid .grid-col-hours .completed-work-row {
            display: flex !important;
            align-items: center !important;
            gap: 0.25rem;
        }

        .work-items-grid .grid-col-hours ::v-deep .form-control {
            width: 2.6rem !important;
            max-width: 2.6rem !important;
            min-width: 2.6rem !important;
            flex: 0 0 2.6rem !important;
            height: 31px !important;
            padding: 0.2rem 0.25rem !important;
            text-align: center;
            font-size: 0.85rem !important;
        }

        .work-items-grid .grid-col-hours ::v-deep .input-group-text {
            padding: 0.2rem 0.3rem !important;
            font-size: 0.75rem !important;
            height: 31px !important;
        }

        /* Sixth row of card: Capacity, Actions, Custom columns */
        .work-items-grid .time-row-shell .grid-row > .grid-col-changecapacity {
            order: 6 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
        }

        .work-items-grid .grid-col-changecapacity ::v-deep .form-control {
            width: 2.6rem !important;
            max-width: 2.6rem !important;
            min-width: 2.6rem !important;
            flex: 0 0 2.6rem !important;
            height: 31px !important;
            padding: 0.2rem 0.25rem !important;
            text-align: center;
            font-size: 0.85rem !important;
        }

        .work-items-grid .grid-col-changecapacity ::v-deep .input-group-text {
            padding: 0.2rem 0.3rem !important;
            font-size: 0.75rem !important;
            height: 31px !important;
        }

        .work-items-grid .time-row-shell .grid-row > .grid-col-buttons-actions {
            order: 6 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            margin-left: auto !important;
        }

        .action-buttons-wrap {
            display: flex !important;
            align-items: center !important;
            gap: 0.25rem;
            height: 31px;
        }

        .work-items-grid .time-row-shell .grid-row > .time-col-custom {
            order: 6 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
        }

        /* Activities Grid Mobile Structure */
        .activity-items-grid .time-row-shell .grid-row > .grid-col-worktype {
            order: 1 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
        }

        .activity-items-grid .grid-col-worktype ::v-deep .custom-select {
            width: 100% !important;
            height: 31px !important;
            font-size: 0.85rem !important;
            max-width: 220px;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-comment {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0.2rem 0 0.4rem 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-hours {
            order: 3 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
        }

        .activity-items-grid .grid-col-hours .completed-work-row {
            display: flex !important;
            align-items: center !important;
            gap: 0.25rem;
        }

        .activity-items-grid .grid-col-hours ::v-deep .form-control {
            width: 2.6rem !important;
            max-width: 2.6rem !important;
            min-width: 2.6rem !important;
            flex: 0 0 2.6rem !important;
            height: 31px !important;
            padding: 0.2rem 0.25rem !important;
            text-align: center;
            font-size: 0.85rem !important;
        }

        .activity-items-grid .grid-col-hours ::v-deep .input-group-text {
            padding: 0.2rem 0.3rem !important;
            font-size: 0.75rem !important;
            height: 31px !important;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-buttons {
            order: 3 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            margin-left: auto !important;
        }

        /* Absences Grid Mobile Structure */
        .absence-items-grid .grid-row > .grid-col-worktype {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            font-weight: 600;
        }

        .absence-items-grid .grid-row > .grid-col-buttons {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            margin-left: auto !important;
        }

        .absence-items-grid .grid-row > .grid-col-hours {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
        }

        .absence-items-grid .grid-col-hours ::v-deep .form-control {
            width: 2.6rem !important;
            max-width: 2.6rem !important;
            min-width: 2.6rem !important;
            flex: 0 0 2.6rem !important;
            height: 31px !important;
            padding: 0.2rem 0.25rem !important;
            text-align: center;
            font-size: 0.85rem !important;
        }

        .absence-items-grid .grid-col-hours ::v-deep .input-group-text {
            padding: 0.2rem 0.3rem !important;
            font-size: 0.75rem !important;
            height: 31px !important;
        }
    }

    @media (min-width: 768px) and (max-width: 991.98px) {
        .work-items-grid .time-row-shell .grid-row > .grid-col-worktype,
        .work-items-grid .time-row-shell .grid-row > .grid-col-hours,
        .work-items-grid .time-row-shell .grid-row > .grid-col-changecapacity,
        .work-items-grid .time-row-shell .grid-row > .time-col-custom {
            order: 4 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            margin-bottom: 0 !important;
        }

        .work-items-grid .time-row-shell .grid-row > .grid-col-buttons-actions {
            order: 4 !important;
            margin-left: auto !important;
            margin-bottom: 0 !important;
        }

        .work-items-grid .grid-col-worktype ::v-deep .custom-select {
            min-width: 130px !important;
            max-width: 175px !important;
            width: auto !important;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-worktype,
        .activity-items-grid .time-row-shell .grid-row > .grid-col-hours {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            margin-bottom: 0 !important;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-buttons {
            order: 1 !important;
            margin-left: auto !important;
        }

        .activity-items-grid .time-row-shell .grid-row > .grid-col-comment {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0.25rem 0 0 0 !important;
        }

        .absence-items-grid .grid-row > .grid-col-worktype,
        .absence-items-grid .grid-row > .grid-col-hours {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            margin-bottom: 0 !important;
        }

        .absence-items-grid .grid-row > .grid-col-buttons {
            order: 1 !important;
            margin-left: auto !important;
        }
    }

    @media (max-width: 359.98px) {
        .time-action-col {
            gap: 0.25rem !important;
            padding-left: 0.05rem !important;
            padding-right: 0.05rem !important;
            justify-content: center !important;
        }

        .time-action-button {
            flex: 0 0 62px !important;
            width: 62px !important;
            min-width: 58px !important;
            max-width: 66px !important;
            min-height: 48px !important;
            height: 48px !important;
            padding: 0.3rem 0.1rem 0.2rem !important;
        }

        .time-action-button .action-btn-icon {
            font-size: 1.25rem !important;
        }

        .time-action-button .action-btn-label {
            font-size: 0.66rem !important;
            letter-spacing: -0.3px !important;
        }
    }
    /* Centered Month Navigation inside Calendar Card */
    .calendar-month-nav-container {
        position: relative;
        width: 100%;
    }

    .calendar-centered-month-header {
        position: absolute;
        top: 0.35rem;
        left: 0;
        right: 0;
        height: 36px;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        z-index: 10;
        pointer-events: auto;
    }

    .calendar-header-title {
        font-size: 1.05rem;
        font-weight: 700;
        letter-spacing: 0.2px;
        color: var(--vt-heading, inherit);
        user-select: none;
    }

    .calendar-nav-arrow-btn {
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
        max-width: 28px !important;
        padding: 0 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 0.4rem !important;
        font-size: 0.85rem !important;
        line-height: 1 !important;
    }

    .time-manager-calendar .b-calendar-grid-caption {
        visibility: hidden !important;
        min-height: 36px !important;
        height: 36px !important;
        margin-bottom: 0.25rem !important;
    }
</style>
